// Detail produktu — redesign podle handoveru Claude Design
// (design_handoff_product/README.md, prototyp `design/Product detail v2.dc.html`
// je závazný; nahrazuje screens/05-product.md + m05-product.md z 1. kola).
//
// Enhancement nad fixním Shoptet DOM `.p-detail > .p-detail-inner`:
//   • galerie „bez rámu": fotka volně na bílé se stínem, žlutá slevová kartička,
//     ink štítky, Bestseller kruh shodný s kartou výpisu, pás 6 miniatur
//   • hlavička nákupního sloupce: řádek kategorie · značka nad H1, hvězdy
//     s částečným vyplněním, dostupnost v meta řádku, krátký popis + odkaz
//   • hlavní karta: cena + časová osa Objednáte → Odešleme → Doručíme (+ countdown
//     do expedice), pás CTA se stepperem a cenou, upsell „Hodí se k tomu"
//     z nativních souvisejících produktů
//   • karta „Potřebujete poradit?" + řádek utility (Zeptat se · Hlídat · značka · kód)
//   • kotvící navigace 7a (podtržené na střed) s počty, scroll-spy
//   • sekce pod sebou místo tabů; poradna (FAQ z popisu) + doplňkové parametry
//     vedle sebe; související soubory jako karty; hodnocení s panelem; karta značky
//     z nativního popisu výrobce; související/podobné jako carousel karet výpisu
//
// Zásady beze změny: žádná vymyšlená data (texty poradce/záruky = schválené
// z designu, CONTACT sdílený; ceny, termín, kód, hodnocení, soubory, značka =
// nativní uzly), nativní uzly se PŘESOUVAJÍ, nekopírují (default JS je u variant
// přepisuje), add-to-cart form se NEPŘEPISUJE — košík obsluhuje výhradně Shoptet
// (`shoptet.cartShared.addToCart` i pro příslušenství z upsellu).
//
// Idempotentní (`data-bs-pd`).

import { icon } from './icon.js';
import { fillAvatar } from './teamPhoto.js';
import { byLabelLength, isActionFlag, isClearanceFlag, oneActionFlag } from './flagOrder.js';
import { activeCampaign, saleLabel, trackPromo } from './campaign.js';
import { MEDIA } from './instanceUrl.js';
import { CONTACT, phonesOn, telHref } from './headerTools.js';
import { createPriceWatch, discountPercent as pctOf, formatPrice, parseNumber, visiblePriceText } from './productPrice.js';
import { lastPiecesLeft } from './productSignals.js';
import { priceLabel, pricesFor, saleNoteText, saveText, wholesaleAdvantage, wholesaleCustomer } from './wholesalePrice.js';
import { VO } from '../locale/cs/common.js';
import SHOP from '../../config/shop.js';
import { stockLevel } from './productCard.js';
import { createSaleEnd } from './saleEnd.js';
import { getRetailConfig } from './searchConfig.js';
import { mountPriceHistory } from './priceHistory.js';
import { mountFreeShipMark } from './freeShipMark.js';
import { specFor, wantReviews } from './specsCache.js';
import { mountDetailRestock } from './restockDate.js';
import { featureOn } from './features.js';
import { mountReviewHighlights, needsShopQuotes, shopQuotes } from './reviewHighlights.js';
import { loadReviews } from './reviews.js';
import {
  deliveryTimeline,
  parseNativeDate,
  countdownLabel,
  fastOffered,
} from './deliveryTimeline.js';
import { loadShippingOptions, pickupWhen, shippingPromo, isPromoRow } from './shippingModel.js';
import { carrierLogo } from './logo.js';
import { initShippingModal } from './shippingModal.js';
import { serviceHref } from './serviceModel.js';
import { initServiceModal } from './serviceModal.js';
import { initUpsellQuickView } from './upsellQuickView.js';
import { addToCart, shoptetData } from '../shoptet-hooks.js';
import { page } from './pages.js';
import { asChatTrigger, chatFallbackHref, chatOn, setChatTopic } from './supportChat.js';
import L from '../locale/cs/productDetail.js';
import LC from '../locale/cs/supportChat.js';
import LCAMP from '../locale/cs/campaign.js';
import { specKeyOf } from './textKey.js';

const LABEL_DETAIL_LINK = L.detailLink;
// Text o servisu pod CTA (handover nákupního boxu §5) — tučně jen první část;
// věta o servisu je od 2026-09-02 podtržená a celý řádek je odkaz na
// /servis-naradi/, který otevírá modal servisu (serviceModal.js)
const LABEL_WARRANTY_STRONG = L.warrantyStrong;
const LABEL_WARRANTY_SEP = ' · ';
const LABEL_WARRANTY_REST = L.warrantyRest;
const LABEL_ADVISOR = L.advisor;
const LABEL_ADVISOR_CTA = L.advisorCta;
// Hlavička poradny (zadání Tomáše 2026-09-04): kdo na otázky odpovídá.
const LABEL_EXPERT = L.expert;
// Poradce = `contact.expert` z manifestu shopu (jméno jen křestní, zadání Tomáše
// 2026-09-10; táž osoba je v týmu na kontaktech) + společná schránka
// `CONTACT.email`. Odpovědi v nativní diskuzi ale administrace podepisuje plným
// jménem, proto se autor příspěvku páruje přes `isExpertName()` (křestní jméno
// i jméno s příjmením), ne prostou shodou.
const EXPERT = SHOP.contact.expert;
const LABEL_EXPERT_CTA = L.askProduct;
const LABEL_MANUAL = L.manual;
// sekce souborů (nativní „Související soubory (N)") → viditelná sekce návodu
const LABEL_FILES_MANUAL = L.filesManual;
const LABEL_FILES_MANUALS = L.filesManuals;
const LABEL_FILES_MIXED = L.filesMixed;
const LABEL_FILES_DOCS = L.filesDocs;
const LABEL_FILE_CTA_MANUAL = L.fileCtaManual;
const LABEL_FILE_CTA_DOC = L.fileCtaDoc;
const LABEL_FILE_CTA_VIDEO = L.fileCtaVideo;
const LABEL_FILE_CTA_LINK = L.fileCtaLink;
const LABEL_FILE_VIDEO = L.fileVideo;
const LABEL_FILE_NEW_WINDOW = L.fileNewWindow;
const MANUAL_RE = L.manualRe;
// „Související soubory" umí nést i externí odkaz (SADPAL: YouTube shorts) —
// tam je „Stáhnout" lež, karta nese play + „Přehrát video"
const VIDEO_RE = /youtube\.com|youtu\.be|vimeo\.com/i;
const FILE_EXT_RE = /^(pdf|docx?|xlsx?|pptx?|txt|rtf|zip|rar|7z|jpe?g|png|gif|svg|csv|odt|ods)$/i;
// hodnocení + diskuze (design 2026-09-05, docs/design/2026-09-05-hodnoceni-diskuze)
const LABEL_RATE_CTA = L.rateCta;
const LABEL_RATE_MORE = L.rateMore;
const LABEL_RATE_SHARE = L.rateShare;
const LABEL_RATE_STARS_ONLY = L.rateStarsOnly;
// Rozhodnutí Tomáše 2026-09-05: v seznamu recenzí jen 3–5 hvězd. Histogram
// v souhrnu zůstává ÚPLNÝ (i 2★ a 1★ řádky) — čísla se nezkreslují, filtr
// platí jen pro slovní recenze pod ním.
const RATING_MIN_STARS = 3;
const LABEL_DISC_CTA = L.askProduct;
// sjednocená karta parametrů (review Tomáše 2026-09-09)
const LABEL_SPECS_TITLE = L.specsTitle;
const LABEL_SPECS_TECH = L.specsTech;
const LABEL_SPECS_EXTRA = L.specsExtra;
const LABEL_DISC_ANSWERS = L.discAnswers;
const LABEL_DISC_SIDE_ROLE = L.discSideRole;
const LABEL_DISC_SIDE_TEXT = L.discSideText;
const LABEL_DISC_URGENT = L.discUrgent;
const LABEL_DISC_PENDING = L.discPending;
// Autor odpovědi z obchodu: jméno nese značku shopu (`shop.brand` z manifestu,
// „Jipos") nebo je to jmenovaná odbornice; značka je i štítek odpovědi.
const LABEL_DISC_SHOP = SHOP.shop.brand;
const SHOP_AUTHOR_RE = new RegExp(SHOP.shop.brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
const LABEL_CODE = L.code;
const LABEL_BRAND = L.brand;
const LABEL_BESTSELLER = L.bestseller;
const LABEL_SALE = L.sale;
const LABEL_SAVE = L.save;
const LABEL_ROW_PREV = L.rowPrev;
const LABEL_ROW_NEXT = L.rowNext;
const LABEL_ROW_ALL = L.rowAll;
const LABEL_ROW_SUB = L.rowSub;
const LABEL_UPSELL = L.upsell;
const LABEL_IN_STOCK = L.inStockSuffix;
// #47 / audit §2: vlaječka „Poslední kusy skladem" — data z dataLayeru
// (`productSignals.js`), v DOM kotva není.
const LABEL_LAST_ONE = L.lastOne;
const LABEL_LAST_PIECES = L.lastPieces;
const LABEL_STEP_ORDER = L.stepOrder;
const LABEL_STEP_SHIP = L.stepShip;
const LABEL_STEP_DELIVER = L.stepDeliver;
const LABEL_SHIPPING_FROM = L.shippingFrom;
const LABEL_SHIPPING_FROM_PREFIX = L.shippingFromPrefix;
const LABEL_PICKUP = L.pickup;
const LABEL_PICKUP_SOON = L.pickupSoon;
const LABEL_FREE = L.free;
const LABEL_PROMO_BOX = L.promoBox;
const LABEL_PROMO_UNTIL = L.promoUntil;
const LABEL_COUNTDOWN = L.countdown;
const LABEL_BRAND_ALL = L.brandAll;
const LABEL_BRAND_TITLE = L.brandTitle;
const LABEL_NAV = L.nav;
// Vyprodaný produkt (#227, mock schválen 2026-08-31 — verze A s panelem)
const LABEL_SOLDOUT_TITLE = L.soldoutTitle;
const LABEL_SOLDOUT_TEXT = L.soldoutText;
const LABEL_SOLDOUT_PRICE = L.soldoutPrice;
const LABEL_SOLDOUT_WATCH = L.soldoutWatch;
const LABEL_SOLDOUT_ASK = L.askProduct;
const LABEL_SOLDOUT_NOTE = L.soldoutNote;
const LABEL_SOLDOUT_STATE = L.soldoutState;
const LABEL_ALT_TITLE = L.altTitle;
const LABEL_ALT_SUB = L.altSub;
const ALT_MAX = 3;

// Kolik thumbnailů se vejde do desktopové mřížky (6. dlaždice = „+ N fotek")
const THUMBS_VISIBLE = 6;
// Upsell bere první tři související produkty skladem
const UPSELL_MAX = 3;
// Obnova countdownu do expedice
const TICK_MS = 30000;
// Rozestup volání addToCart u příslušenství (nativní handler košíku běží AJAX)
const ADD_GAP_MS = 900;

// Kotvy lišty v pořadí z designu; bere se jen to, co na stránce existuje.
// `parametry` a `poradna` mohou přijít z popisu produktu (docs/sablony/produkt-popis.html),
// ostatní jsou nativní panely / naše hlavičky carouselů. Id se nepřepisují.
// `icon` = vlastní glyph každé sekce vlevo vedle textu (zadání Tomáše 2026-09-05,
// symboly v spritu ve stylu root sady); `accent` = pilulka jako plná červená
// (Návod — stejné zvýraznění jako tlačítko v kartě poradny a sekce návodu).
const ANCHORS = [
  { id: 'description', label: L.anchors.description, icon: 'text' },
  { id: 'parametry', label: L.anchors.parametry, icon: 'sliders' },
  { id: 'poradna', label: L.anchors.poradna, count: 'faq', icon: 'help' },
  { id: 'relatedFiles', label: L.anchors.relatedFiles, icon: 'book', accent: true },
  { id: 'ratingTab', label: L.anchors.ratingTab, count: 'rating', icon: 'star-line' },
  { id: 'productDiscussion', label: L.anchors.productDiscussion, count: 'discussion', icon: 'chats' },
  { id: 'manufacturerDescription', label: L.anchors.manufacturerDescription, icon: 'badge' },
  { id: 'souvisejici', label: L.anchors.souvisejici, count: 'related', icon: 'link' },
  { id: 'podobne', label: L.anchors.podobne, count: 'alternative', icon: 'copy' },
];

// Ikony dlaždic sortimentu značky podle klíčových slov titulku (bez shody = cog)
const BRAND_TILE_ICONS = L.brandTileIcons;

function text(el) {
  return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

function reducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function scrollTrack(track, left) {
  if (track.scrollTo) track.scrollTo({ left: left, behavior: reducedMotion() ? 'auto' : 'smooth' });
  else track.scrollLeft = left;
}

// poslední odkaz breadcrumbs = nadřazená kategorie (řádek nad H1 i „Zobrazit vše")
function parentCategoryLink() {
  const crumbs = [...document.querySelectorAll('.breadcrumbs a')].filter(function (a) {
    return text(a) && !a.classList.contains('navigation-home-icon');
  });
  return crumbs[crumbs.length - 1] || null;
}

function node(tag, className, content) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (content != null) el.textContent = content;
  return el;
}

function link(href, className, content) {
  const el = node('a', className, content);
  el.href = href;
  return el;
}

// „+ 8 fotek" / „+ 2 fotky" / „+ 1 foto"
function photosLabel(n) {
  return L.photos(n);
}

// „−34 %" z nativního `.price-save` („(–34 %)") nebo z nativní vlaječky slevy
function discountPercent(form, image) {
  const sources = [form && form.querySelector('.price-save'), image && image.querySelector('.flags .flag-discount')];
  for (let i = 0; i < sources.length; i += 1) {
    const m = text(sources[i]).match(/(\d+)\s*%/);
    if (m) return parseInt(m[1], 10);
  }
  if (form) {
    const std = parseNumber(visiblePriceText(form.querySelector('.price-standard')));
    const now = parseNumber(visiblePriceText(form.querySelector('.price-final')));
    return pctOf(std, now); // zaokrouhlení dolů jako Shoptet
  }
  return null;
}

// Hvězdy s částečným vyplněním (sdílený primitiv .bs-stars, _stars.scss)
function stars(value, className) {
  const wrap = node('span', 'bs-stars' + (className ? ' ' + className : ''), '★★★★★');
  wrap.setAttribute('aria-hidden', 'true');
  const fill = node('span', 'bs-stars__fill', '★★★★★');
  wrap.style.setProperty('--bs-stars-pct', Math.round((value / 5) * 1000) / 10 + '%');
  wrap.appendChild(fill);
  return wrap;
}

// Průměr z nativního `.sr-only` („Průměrné hodnocení produktu je 4,2 z 5…")
// nebo z panelu hodnocení (`.rate-average`); 0 = bez hodnocení.
function ratingAverage() {
  const sr = document.querySelector('.p-detail-inner-header .stars .sr-only, #ratingWrapper .stars .sr-only');
  let m = text(sr).match(L.ratingAvgRe);
  if (!m) m = text(document.querySelector('#ratingWrapper .rate-average')).match(/(\d+(?:[.,]\d+)?)/);
  const v = m ? parseFloat(m[1].replace(',', '.')) : 0;
  return isFinite(v) ? v : 0;
}

// Počet hodnocení: „89 hodnocení" (label) nebo „Hodnocení (89)" (tab)
function ratingCount() {
  let m = text(document.querySelector('.p-detail-inner-header .stars-label')).match(/(\d+)/);
  if (!m) m = text(document.querySelector('.shp-tab-link[href="#ratingTab"], .shp-accordion-link[href="#ratingTab"]')).match(/\((\d+)\)/);
  return m ? parseInt(m[1], 10) : 0;
}

// ---------------------------------------------------------------------------
// Galerie
// ---------------------------------------------------------------------------

function buildBadges(image, form) {
  const box = node('div', 'bs-pd__badges');

  const flags = Array.prototype.filter.call(image.querySelectorAll('.flags .flag'), function (flag) {
    if (flag.classList.contains('flag-discount')) return false; // sleva jede žlutou kartičkou
    if (flag.classList.contains('flag-tip')) return false; // TIP se nekreslí — Bestseller dává index (mountSticker)
    return true;
  });
  // jeden akční štítek (výprodej skladu před Slevoakcí, flagOrder.js); vyřazená
  // vlaječka zůstává ve skrytém nativním `.flags`; po dobu kampaně nese akční
  // nabídka název deštníku (mock M8 b), výprodej skladu je vždy žlutý
  const saleText = saleLabel();
  byLabelLength(oneActionFlag(flags), function (flag) {
    return (isActionFlag(flag) && saleText) || flag.textContent;
  }).forEach(function (flag) {
    flag.classList.add('bs-badge');
    if (isClearanceFlag(flag)) flag.classList.add('bs-badge--clearance');
    else if (flag.classList.contains('flag-new')) flag.classList.add('bs-badge--outline');
    else flag.classList.add('bs-badge--ink');
    if (isActionFlag(flag) && saleText) flag.textContent = saleText;
    box.appendChild(flag);
  });
  if (box.children.length) image.appendChild(box);

  // žlutá kartička „−34 % / sleva" vpravo nahoře
  const pct = discountPercent(form, image);
  if (pct) {
    const card = node('span', 'bs-pd__sale');
    card.setAttribute('aria-hidden', 'true');
    card.appendChild(node('span', 'bs-pd__sale-pct', '−' + pct + ' %'));
    card.appendChild(node('span', 'bs-pd__sale-label', LABEL_SALE));
    image.appendChild(card);
  }
}

/**
 * Bestseller kruh (shodný s kartou výpisu). Zdroj je INDEX (`/specs` →
 * `bestseller: true` = top výchozí kategorie dle tržby za 90 dní), ne nativní
 * TIP — ten nese 85 % katalogu. Offline/harness: `.p-detail-inner[data-bs-bestseller="1"]`.
 */
function mountSticker(image) {
  if (!image || image.querySelector('.bs-pd__sticker')) return;
  const sticker = node('span', 'bs-pd__sticker');
  sticker.setAttribute('aria-hidden', 'true');
  sticker.appendChild(node('span', 'bs-pd__sticker-star', '★'));
  sticker.appendChild(node('span', 'bs-pd__sticker-label', LABEL_BESTSELLER));
  image.appendChild(sticker);
}

function buildGallery(wrapper, form) {
  const image = wrapper.querySelector('.p-image');
  if (image) {
    image.classList.add('bs-pd__main');
    if (!image.querySelector('img')) image.appendChild(icon('photo', 'bs-pd__ph'));
    // měkký stín pod strojem (varianta „Bez rámu")
    const shadow = node('span', 'bs-pd__shadow');
    shadow.setAttribute('aria-hidden', 'true');
    image.insertAdjacentElement('afterbegin', shadow);
    buildBadges(image, form);
  }

  const strip = wrapper.querySelector('.p-thumbnails-inner > div');
  if (!strip) return;
  strip.classList.add('bs-pd-thumbs');

  const thumbs = [...strip.querySelectorAll('a.p-thumbnail')];
  const boxes = [...strip.querySelectorAll('a.cbox-gal')];
  thumbs.forEach(function (thumb, i) {
    thumb.classList.add('bs-pd-thumb');
    // nad desktopovou mřížku se schová CSS; na mobilu jede snap pás se všemi
    if (i >= THUMBS_VISIBLE - 1) thumb.setAttribute('data-bs-thumb-over', String(i));
  });

  const hidden = thumbs.length - (THUMBS_VISIBLE - 1);
  if (hidden > 0 && boxes.length) {
    const more = node('button', 'bs-pd-thumbs__more');
    more.type = 'button';
    more.appendChild(icon('photo'));
    more.appendChild(node('span', null, '+ ' + hidden + ' ' + photosLabel(hidden)));
    // klik na `a.cbox-gal` zachytí náš lightbox (galleryLightbox.js, capture fáze)
    const target = boxes[Math.min(THUMBS_VISIBLE - 1, boxes.length - 1)];
    more.addEventListener('click', function () {
      target.click();
    });
    strip.appendChild(more);
  }
}

// ---------------------------------------------------------------------------
// Hlavička nákupního sloupce (řádek kategorie · značka, H1, hodnocení, sklad)
// ---------------------------------------------------------------------------

function buildHeader(data, form) {
  const head = data.querySelector('.p-detail-inner-header');
  if (head) head.classList.add('bs-pd__head');

  // řádek nad titulkem: kategorie (červeně) · značka (šedě)
  const parent = parentCategoryLink();
  const brandLink = data.querySelector('.p-detail-inner-header .brand-wrapper a');
  if (head && (parent || brandLink)) {
    const eyebrow = node('div', 'bs-pd__eyebrow');
    if (parent) eyebrow.appendChild(link(parent.getAttribute('href'), 'bs-pd__eyebrow-cat', text(parent)));
    if (parent && brandLink) eyebrow.appendChild(node('span', 'bs-pd__eyebrow-dot', '·'));
    if (brandLink) eyebrow.appendChild(link(brandLink.getAttribute('href'), 'bs-pd__eyebrow-brand', text(brandLink)));
    head.insertAdjacentElement('afterbegin', eyebrow);
  }

  const ratings = data.querySelector('.p-detail-inner-header .ratings-and-brand');
  if (ratings) {
    ratings.classList.add('bs-pd__meta');
    const starsWrap = ratings.querySelector('.stars-wrapper');
    if (starsWrap) {
      starsWrap.classList.add('bs-pd__rating');
      const avg = ratingAverage();
      const native = starsWrap.querySelector('.stars.star-list');
      if (avg > 0 && native) {
        native.insertAdjacentElement('beforebegin', stars(avg, 'bs-stars--pd'));
        native.hidden = true;
      }
      // „89 hodnocení" je v prototypu odkaz na sekci hodnocení
      const label = starsWrap.querySelector('.stars-label');
      if (label && ratingCount() > 0 && document.getElementById('ratingTab')) {
        const a = link('#ratingTab', 'bs-pd__rating-count', text(label));
        label.textContent = '';
        label.appendChild(a);
      }
    }
  }

  // dostupnost: nativní uzly z tabulky formu do meta řádku (ikona balíku).
  // Variantní produkt nese v buňce jeden `span.choose-variant.<index>` na
  // variantu + `.default-variant` („Zvolte variantu") a default JS jim při volbě
  // přepíná `no-display` — jen uvnitř `.p-detail-inner` a `.p-code`, kam meta
  // řádek patří. Přesouvají se proto VŠECHNY skupiny (do 2026-09-24 jen první
  // štítek: pilulka trvale hlásila stav první velikosti, u holin „Momentálně
  // nedostupné", i když 43 skladem byla) a barvu určuje ta viditelná (`paintStock`).
  const table = form && form.querySelector('table.detail-parameters');
  const label = table && table.querySelector('.availability-label');
  const amount = table && table.querySelector('.availability-amount');
  if (ratings && label) {
    const row = label.closest('tr'); // před přesunem — potom už `closest` tabulku nenajde
    const cell = label.closest('td');
    const sep = node('span', 'bs-pd__meta-sep');
    sep.setAttribute('aria-hidden', 'true');
    ratings.appendChild(sep);
    const stock = node('span', 'bs-pd__stock');
    stock.appendChild(icon('package'));
    const groups = cell ? [...cell.children].filter((el) => el.classList.contains('choose-variant')) : [];
    if (groups.length) {
      groups.forEach((g) => stock.appendChild(g));
      // před volbou varianty souhrn místo nativního „Zvolte variantu"
      stock.appendChild(node('span', 'bs-pd__stock-sum'));
    } else {
      stock.appendChild(label);
      if (amount) stock.appendChild(amount);
    }
    // nativní inline barva (#009901 = skladem) → třída; jiné stavy (na dotaz)
    // si inline barvu nechávají, ať se neztratí význam
    stock.querySelectorAll('.availability-label').forEach((el) => {
      if (isStockLabel(el) || L.soldOutRe.test(text(el))) el.removeAttribute('style');
    });
    ratings.appendChild(stock);
    paintStock(stock);
    // volba varianty = přepnutí `no-display` → přebarvit; paint zapisuje jen
    // změnu, jinak by vlastní zápis `className` observer roztočil
    if (groups.length) {
      new MutationObserver(function () {
        paintStock(stock);
      }).observe(stock, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
    if (row) row.remove();
  }

  // „Poslední kusy skladem" (#47, audit §2 bod 1). Nativní řádek dostupnosti
  // hlásí jen „Skladem (1 ks)", tíseň z toho čtenář sám neudělá — karta výpisu
  // na to má tři pásma proužku zásoby (#174), detail neměl nic. Počet nese
  // dataLayer, ne DOM; nekreslí se, když je množství rozostřené („> 5").
  const left = lastPiecesLeft(shoptetData('product'));
  if (ratings && left != null) {
    const flag = node('span', 'bs-pd__last');
    flag.appendChild(icon('bolt'));
    flag.appendChild(node('span', null, left === 1 ? LABEL_LAST_ONE : LABEL_LAST_PIECES));
    ratings.appendChild(flag);
  }

  const short = data.querySelector('.p-short-description');
  if (short && document.getElementById('description')) {
    short.classList.add('bs-pd__short');
    const more = link('#description', 'bs-pd__more', LABEL_DETAIL_LINK + ' ');
    more.appendChild(node('span', 'bs-pd__more-arrow', '↓'));
    const lastP = short.querySelector('p:last-of-type');
    const host = lastP || short;
    host.appendChild(document.createTextNode(' '));
    host.appendChild(more);
  }
}

// ---------------------------------------------------------------------------
// Hlavní karta: cena + časová osa | CTA pás | upsell
// ---------------------------------------------------------------------------

function hasPickup(form) {
  return [...form.querySelectorAll('meta[itemprop="shippingLabel"]')].some(function (meta) {
    return L.pickupRe.test(meta.getAttribute('content') || '');
  });
}

// Krok časové osy: ikona v kroužku + spojnice + řádek popisek / hodnota.
// `pending` = spojnice k dalšímu kroku je šedá (krok, který teprve přijde).
function buildStep(glyph, label, opts) {
  const o = opts || {};
  const step = node('div', 'bs-buy__step' + (o.last ? ' bs-buy__step--last' : ''));
  const col = node('span', 'bs-buy__step-col');
  const dot = node('span', 'bs-buy__dot' + (o.last ? ' bs-buy__dot--outline' : ''));
  dot.appendChild(icon(glyph));
  col.appendChild(dot);
  if (!o.last) col.appendChild(node('span', 'bs-buy__line' + (o.pending ? ' bs-buy__line--pending' : '')));
  step.appendChild(col);
  const row = node('span', 'bs-buy__step-row');
  row.appendChild(node('span', 'bs-buy__step-label', label));
  const val = node('span', 'bs-buy__step-val');
  // hodnota + inline štítek vedle ní (zvláštní závoz dopravce) na JEDNOM řádku;
  // štítky pod hodnotou (odpočet) jdou přímo do `val`
  const line = node('span', 'bs-buy__step-line');
  const value = node('b', 'bs-buy__step-value');
  line.appendChild(value);
  val.appendChild(line);
  row.appendChild(val);
  step.appendChild(row);
  return { el: step, value: value, val: val, line: line };
}

// Je zboží skladem? Táž detekce jako v `buildHeader` (nativní text + inline barva
// #009901), ale NESMÍ viset na tabulce formu: `buildHeader` běží dřív a uzel
// `.availability-label` si z ní odstěhuje do meta řádku `.bs-pd__stock`, takže
// `table.querySelector()` už vrátí null (a odstraní i inline `style`). Detail
// nese uzel právě jeden, proto je fallback na dokument bezpečný; rozhoduje text
// „Skladem", který přesun i odbarvení přežije.
//
// Variantní produkt: skladem = skladem aspoň jedna varianta (fráze odběru platí
// pro to, co jde koupit; konkrétní velikost ukáže pilulka po volbě).
function isInStock(table) {
  let labels = table ? [...table.querySelectorAll('.availability-label')] : [];
  if (!labels.length) labels = [...document.querySelectorAll('.bs-pd__stock .availability-label')];
  if (!labels.length && document.querySelector('.availability-label')) labels = [document.querySelector('.availability-label')];
  return labels.some(isStockLabel);
}

function isStockLabel(label) {
  return !!label && (L.inStockRe.test(text(label))
    || /#009901|rgb\(0,\s*153,\s*1\)/i.test(label.getAttribute('style') || ''));
}

// Pilulka dostupnosti podle zobrazeného stavu. Bez variant = jediný štítek;
// s variantami = viditelná skupina `.choose-variant.<index>`, a dokud zákazník
// nevybral (`.default-variant`), souhrn: nativní štítek první varianty skladem
// bez počtu kusů, jinak „Momentálně nedostupné". Zapisuje jen změnu (observer).
function paintStock(stock) {
  const groups = [...stock.children].filter((el) => el.classList.contains('choose-variant'));
  const shown = groups.find((g) => !g.classList.contains('no-display'));
  const sum = stock.querySelector('.bs-pd__stock-sum');
  let label = null;
  let amount = null;
  let summary = false;
  if (!groups.length) {
    label = stock.querySelector('.availability-label');
    amount = stock.querySelector('.availability-amount');
  } else if (shown && !shown.classList.contains('default-variant')) {
    label = shown.querySelector('.availability-label');
    amount = shown.querySelector('.availability-amount');
  } else {
    summary = true;
    label = groups.map((g) => g.querySelector('.availability-label')).find(isStockLabel) || null;
  }
  const inStock = isStockLabel(label);
  const soldOut = summary ? !inStock : L.soldOutRe.test(text(label));
  const cls = ['bs-pd__stock'];
  if (summary) cls.push('bs-pd__stock--summary');
  if (inStock) {
    // Tři pásma podle počtu kusů — TÁŽ pravidla i barvy jako proužek zásoby
    // na kartě výpisu (#174): ≥ 5 zelená, 2–4 žlutá, 1 ks červená. Detail byl
    // do teď vždy zelený, takže „1 ks" vypadal stejně zdravě jako „> 5 ks"
    // (zadání Tomáše 2026-08-31). Souhrn variant počet nemá → zelená.
    cls.push('is-in-stock', 'bs-pd__stock--' + (summary ? 'ok' : stockLevel(text(amount)).tier));
  }
  // vyprodáno (#227): stav jako výrazná pilulka s hodinami, ne zelený balík
  if (soldOut) cls.push('is-soldout');
  const next = cls.join(' ');
  if (stock.className !== next) stock.className = next;
  const glyph = soldOut ? 'clock' : 'package';
  if (stock.firstChild && stock.firstChild.dataset.bsGlyph !== glyph) {
    const ic = icon(glyph);
    ic.dataset.bsGlyph = glyph;
    stock.replaceChild(ic, stock.firstChild);
  }
  if (sum) {
    const words = summary ? (inStock ? text(label) : LABEL_SOLDOUT_STATE) : '';
    if (sum.textContent !== words) sum.textContent = words;
  }
}

// „Doprava od 139 Kč · odběr v Bílině již zítra zdarma · Možnosti doručení" — cena
// dopravy se čte z nativní stránky možností doručení (`a.shipping-options`), ne
// z konstanty: nadrozměrné zboží má jinou nejlevnější dopravu než drobné.
//
// Termín u osobního odběru jde z otevírací doby prodejny (`pickupWhen` →
// `nextPickupDay`, CONTACT.openHours včetně víkendu), NE z nativního
// `div.delivery-time` — to je termín kurýra, ne připravení na prodejně
// (CLAUDE.md) — a od 2026-09-02 ani z expedice skladu: zboží skladem leží na
// prodejně připravené (rozhodnutí Tomáše 2026-08-31, PICKUP_STOCK_NOTE), takže
// v pátek večer je k odběru v sobotu, ne „v pondělí", a fráze sedí s modalem
// „Možnosti doručení" i s krokem 2 pokladny. Neskladové zboží frázi NEDOSTÁVÁ —
// kdy dorazí naskladnění, nevíme (termín z objednávek u dodavatelů nese jen
// panel vyprodaného, restockDate.js).
function fillShippingNote(note, shippingLink, form, row, table) {
  const pickup = hasPickup(form);
  const inStock = isInStock(table);
  // Tlačítko je SOUROZENEC textu v řádku (text `flex: 1` ho odtlačí k pravé
  // hraně) — uvnitř textového spanu by se lepilo za poslední slovo.
  if (shippingLink && row) {
    shippingLink.classList.add('bs-buy__note-link');
    shippingLink.insertBefore(icon('truck'), shippingLink.firstChild);
    row.appendChild(shippingLink);
  }
  function render(min, rowsPickup) {
    note.textContent = '';
    const parts = [];
    if (isFinite(min) && min > 0) {
      note.appendChild(document.createTextNode(LABEL_SHIPPING_FROM + ' '));
      note.appendChild(node('b', 'bs-buy__note-strong', LABEL_SHIPPING_FROM_PREFIX + ' ' + formatPrice(min)));
      parts.push(1);
    } else if (isFinite(min) && min === 0) {
      note.appendChild(document.createTextNode(LABEL_SHIPPING_FROM + ' '));
      note.appendChild(node('b', 'bs-buy__note-free', LABEL_FREE));
      parts.push(1);
    }
    if (pickup || rowsPickup) {
      if (parts.length) note.appendChild(document.createTextNode(' · '));
      // fráze se počítá při každém renderu, ne jednou při initu — karta může na
      // stránce viset přes cut-off i přes půlnoc
      const when = inStock ? pickupWhen() : '';
      const label = when ? LABEL_PICKUP + ' ' + LABEL_PICKUP_SOON + ' ' + when : LABEL_PICKUP;
      note.appendChild(document.createTextNode(label + ' '));
      note.appendChild(node('b', 'bs-buy__note-free', LABEL_FREE));
      parts.push(1);
    }
  }
  // Akce na dopravu (#322, mock M2): nabídka produktu potvrdila výdejní místo
  // dopravce akce za akční cenu → řádek dopravy se změní na pruh akce s logem
  // dopravce a přeškrtnutou cenou ceníku. Rozhoduje odpověď platformy
  // (`isPromoRow`), ne okno samo: zboží nad prahem zdarma má v nabídce 0,
  // těžké zboží cenu vyššího pásma, a ani jedno akci nedostane. Pruh nahrazuje
  // „Doprava od" (žádný řádek navíc, kostra detailu drží výšku).
  function renderPromo(pr) {
    note.textContent = '';
    if (row && !row.classList.contains('bs-buy__note--promo')) {
      row.classList.add('bs-buy__note--promo');
      // logo dopravce místo náklaďáku (ikona zůstává v DOM, CSS ji skryje)
      const mark = carrierLogo(pr.carrier, 'bs-buy__note-logo');
      if (mark) row.insertBefore(mark, note);
    }
    // 1. řádek: cena (přeškrtnutá cena ceníku za ní), 2. řádek: rozsah a konec;
    // jméno dopravce v textu jen bez loga (jinak by stálo „PPL“ dvakrát)
    const hasLogo = !!(row && row.querySelector('.bs-buy__note-logo'));
    const l1 = node('span', 'bs-buy__note-l1');
    l1.appendChild(document.createTextNode(LABEL_PROMO_BOX(hasLogo ? '' : pr.carrier) + ' '));
    l1.appendChild(node('b', 'bs-buy__note-promo', formatPrice(pr.price)));
    l1.appendChild(document.createTextNode(' '));
    l1.appendChild(node('s', 'bs-buy__note-was', formatPrice(pr.was)));
    note.appendChild(l1);
    note.appendChild(node('span', 'bs-buy__note-l2', LABEL_PROMO_UNTIL(pr.untilLabel, pr.maxKg)));
  }
  render(NaN, false);
  if (!shippingLink) return;
  // Tentýž dokument otevírá i modal „Možnosti doručení" (shippingModal.js) —
  // fetch je sdílený a cachovaný, druhý request nevzniká.
  loadShippingOptions(shippingLink.getAttribute('href')).then(function (data) {
    if (!data) return; // bez sítě zůstává jen osobní odběr + odkaz
    const pr = shippingPromo();
    if (
      pr &&
      data.rows.some(function (r) {
        return isPromoRow(r, pr);
      })
    ) {
      renderPromo(pr);
      return;
    }
    let min = Infinity;
    let rowsPickup = false;
    data.rows.forEach(function (row) {
      const isPickup = row.group === 'pickup';
      if (isPickup) rowsPickup = true;
      const price = typeof row.price === 'number' ? row.price : NaN;
      if (!isPickup && isFinite(price) && price < min) min = price;
    });
    render(isFinite(min) ? min : NaN, rowsPickup);
  });
}

function buildTimeline(form, table) {
  const ship = node('div', 'bs-buy__ship');
  const steps = [
    buildStep('check', LABEL_STEP_ORDER, {}),
    // spojnice Odešleme → Doručíme je šedá: doručení jako jediný krok teprve přijde
    buildStep('package', LABEL_STEP_SHIP, { pending: true }),
    buildStep('home', LABEL_STEP_DELIVER, { last: true }),
  ];
  steps.forEach(function (s) {
    ship.appendChild(s.el);
  });

  // nativní termín zůstává v DOM (skrytý) — default JS ho u variant přepisuje.
  // Řádky tabulky si zapamatovat PŘED přesunem uzlů (pak už `closest('tr')` nic nenajde).
  const delivery = table && table.querySelector('.delivery-time');
  const shippingLink = table && table.querySelector('a.shipping-options');
  const consumedRows = [delivery, shippingLink].map(function (el) {
    return el ? el.closest('tr') : null;
  });
  if (delivery) {
    delivery.hidden = true;
    ship.appendChild(delivery);
  }

  // Řádek dopravy stojí POD osou přes celou šířku karty (handover §4), proto ho
  // timeline jen postaví a vrátí — umístí ho `buildBuyCard`.
  const note = node('div', 'bs-buy__note');
  note.appendChild(icon('truck'));
  const noteText = node('span', 'bs-buy__note-text');
  note.appendChild(noteText);

  // odpočet do expedice = štítek pod hodnotou prvního kroku (handover §3)
  const cd = node('span', 'bs-buy__cd');
  cd.hidden = true;
  cd.appendChild(icon('clock'));
  cd.appendChild(document.createTextNode(LABEL_COUNTDOWN + ' '));
  const cdValue = node('b', 'bs-buy__cd-value');
  cd.appendChild(cdValue);
  steps[0].val.appendChild(cd);

  // Zvláštní závoz dopravce (deliveryTimeline.js `t.fast`): GLS sobota (okno
  // čtvrtek 15:00 → pátek 14:30) nese krok „Doručíme" sobotní termín „v sobotu
  // 5. 9."; PPL neděle (pátek 14:30 → sobota 24:00) překlopí celou osu —
  // „Objednáte dnes do půlnoci" (+ odpočet do půlnoci), „Odešleme zítra 6. 9."
  // (neděle), „Doručíme v pondělí 7. 9.". Kroky nesou JEN DATUM — štítek
  // s dopravcem („s GLS" / „s PPL", od 2026-09-05 logem) osa od 2026-09-05
  // nekreslí (zadání Tomáše: značka patří k řádku dopravce v modalu, ne k datu
  // na ose). Že je dopravce u produktu v nabídce, hlídá fetch modalu níž
  // (`data-bs-carriers`); bez něj drží osa běžný (nativní) termín, ať
  // nadrozměr (jen Fofr) závoz neslíbí.
  fillShippingNote(noteText, shippingLink, form, note, table);

  function carriers() {
    return (ship.dataset.bsCarriers || '').split('|').filter(Boolean);
  }

  function render() {
    const nativeDate = delivery ? parseNativeDate(text(delivery)) : null;
    const t = deliveryTimeline(new Date(), nativeDate);
    const fast = fastOffered(t, carriers()) ? t.fast : null;
    steps[0].value.textContent = fast ? fast.order : t.order;
    steps[1].value.textContent = fast ? fast.ship : t.ship;
    steps[2].value.textContent = fast ? fast.deliver : t.deliver;
    ship.classList.toggle('bs-buy__ship--fast', !!fast);
    const cdOn = fast ? fast.countdown : t.countdown;
    cd.hidden = !cdOn;
    cdValue.textContent = countdownLabel(cdOn);
  }
  render();
  // Které metody má produkt v nabídce? Bez dopravce v nabídce štítek nesvítí
  // (nadrozměr veze jen Fofr). Názvy se drží v DOM (`data-bs-carriers`), aby
  // tick po překlopení okna (pátek 14:30, sobota 24:00) rozhodl znovu. Fetch
  // sdílí a cachuje shippingModel — druhý request nevzniká.
  if (shippingLink) {
    loadShippingOptions(shippingLink.getAttribute('href')).then(function (data) {
      const rows = (data && data.rows) || [];
      const names = rows
        .map(function (r) {
          return String(r.name || '').replace(/\|/g, ' ');
        })
        .filter(Boolean);
      if (!names.length || ship.dataset.bsCarriers) return;
      ship.dataset.bsCarriers = names.join('|');
      render();
    });
  }
  setInterval(render, TICK_MS);
  if (delivery) new MutationObserver(render).observe(delivery, { childList: true, subtree: true, characterData: true });

  // spotřebované řádky nativní tabulky pryč
  consumedRows.forEach(function (row) {
    if (row && row.isConnected) row.remove();
  });
  return { el: ship, note: note };
}

// Upsell „Hodí se k tomu:" — první tři související produkty skladem. Data jdou
// z nativních karet (`.products-related .p[data-micro=product]`), nic se nepíše
// ručně. Vrací { el, sum() } — součet vybraných položek pro cenu v CTA.
//
// ⛔ `capPrice` = cena hlavního produktu, nabídka nesmí být dražší. Shoptet má
// zapnuté obousměrné párování, takže u příslušenství visí v souvisejících
// i výrobky, ke kterým bylo připnuté (nůž C-NOZE za 179 Kč → 4 kalová čerpadla
// za 1 399–2 490 Kč, sonda 2026-09-21 `ops/sweeps/2026-09-21-kosik-cross-sell-faze0/`
// §0.2). Totéž pravidlo drží řádek v košíku (`cartCrossSellModel.js`).
// Neznámá cena (NaN) strop vypíná.
function buildUpsell(capPrice) {
  const cards = [...document.querySelectorAll('.products-related .p[data-micro="product"]')];
  const items = [];
  cards.forEach(function (card) {
    if (items.length >= UPSELL_MAX) return;
    const code = text(card.querySelector('.p-code [data-micro="sku"]'));
    const name = text(card.querySelector('a.name'));
    const offer = card.querySelector('[data-micro="offer"]');
    const price = parseNumber(offer ? offer.getAttribute('data-micro-price') : text(card.querySelector('.price-final')));
    const avail = card.querySelector('.availability');
    const inStock = !!avail && (avail.classList.contains('is-in-stock') || L.inStockRe.test(text(avail)));
    const img = card.querySelector('a.image img');
    const href = (card.querySelector('a.name') || {}).getAttribute ? card.querySelector('a.name').getAttribute('href') : '';
    // Bez nativního `form.pr-action` produkt kódem do košíku přidat NELZE —
    // je to variantní kus a `[data-micro=sku]` na kartě nese master kód
    // (`P500-20/M`), který by `addToCart` odmítl. Stejný signál používá
    // `cartModal.extractUpsell` (`hasForm`).
    const addable = !!card.querySelector('form.pr-action');
    if (!code || !name || !isFinite(price) || !inStock || !addable) return;
    if (isFinite(capPrice) && price > capPrice) return;
    items.push({ code: code, name: name, price: price, img: img ? img.getAttribute('src') : '', href: href, selected: false });
  });
  if (!items.length) return null;

  const box = node('div', 'bs-buy__upsell');
  box.appendChild(node('div', 'bs-buy__upsell-title', LABEL_UPSELL));
  const listeners = [];
  items.forEach(function (it, i) {
    const row = node('label', 'bs-buy__acc');
    // seed rychlého náhledu (upsellQuickView.js) — modal ukáže název, fotku
    // a cenu hned, než doběhne fetch detailu
    row.dataset.bsCode = it.code;
    row.dataset.bsName = it.name;
    row.dataset.bsPrice = String(it.price);
    row.dataset.bsStock = LABEL_IN_STOCK;
    const input = node('input', 'bs-buy__acc-input');
    input.type = 'checkbox';
    input.value = it.code;
    input.setAttribute('aria-label', it.name);
    row.appendChild(input);
    const check = node('span', 'bs-buy__acc-box');
    check.appendChild(icon('check'));
    row.appendChild(check);
    // Fotka i název jsou odkazy na detail a otevírají rychlý náhled
    // (upsellQuickView.js); zbytek řádku dál přepíná výběr. ⚠️ Vědomá odchylka
    // od design_handoff_product §upsell („klik kamkoli na řádek přepíná
    // výběr") — rozhodl Tomáš 2026-09-12, nevracet.
    const thumb = node('a', 'bs-buy__acc-img');
    thumb.href = it.href;
    thumb.dataset.bsQv = '1';
    thumb.tabIndex = -1;
    thumb.setAttribute('aria-hidden', 'true');
    if (it.img) {
      const img = node('img');
      img.src = it.img;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 36;
      img.height = 36;
      thumb.appendChild(img);
    } else {
      thumb.appendChild(icon('photo'));
    }
    row.appendChild(thumb);
    const name = node('a', 'bs-buy__acc-name', it.name + ' ');
    name.href = it.href;
    name.dataset.bsQv = '1';
    name.appendChild(node('span', 'bs-buy__acc-stock', LABEL_IN_STOCK));
    row.appendChild(name);
    row.appendChild(node('span', 'bs-buy__acc-price', '+ ' + formatPrice(it.price)));
    input.addEventListener('change', function () {
      it.selected = input.checked;
      row.classList.toggle('is-selected', input.checked);
      listeners.forEach(function (fn) {
        fn();
      });
    });
    box.appendChild(row);
  });

  return {
    el: box,
    items: items,
    sum: function () {
      return items.reduce(function (s, it) {
        return s + (it.selected ? it.price : 0);
      }, 0);
    },
    selected: function () {
      return items.filter(function (it) {
        return it.selected;
      });
    },
    onChange: function (fn) {
      listeners.push(fn);
    },
  };
}

// ---------------------------------------------------------------------------
// Množstevní slevy (#228, mock schválen 2026-08-31): nativní blok
// `.quantity-discounts` (žije uvnitř formu, v blank módu holý text v toku) se
// PŘESOUVÁ do nákupní karty a pásma jsou klikací — klik nastaví stepper na
// spodní hranici pásma. Zvýraznění i „Ušetříte" počítáme sami z data-amount /
// data-price-ratio (vše v DOM); amount input zůstává zdroj pravdy, košík
// přepočítává Shoptet (cart položka nese quantityRatio).
// ---------------------------------------------------------------------------
function buildQtyDiscounts(form) {
  const box = form.querySelector('.quantity-discounts');
  if (!box) return null;
  box.classList.add('bs-qd');

  const amount = form.querySelector('input.amount');
  const savedEl = box.querySelector('.quantity-discounts__saved-amount');
  const saveRow = box.querySelector('.quantity-discounts__save');
  const orig = parseNumber(
    (box.querySelector('.quantity-discounts__table') || {}).getAttribute
      ? box.querySelector('.quantity-discounts__table').getAttribute('data-orig-price')
      : '',
  );
  const items = [...box.querySelectorAll('.quantity-discounts__item')].map(function (el) {
    // „5 - 9 ks = sleva 5 %" → rozsah v titulku + červený badge „−5 %" (mock)
    const title = el.querySelector('.quantity-discounts__title');
    const m = title ? text(title).match(L.qtyBandRe) : null;
    if (m) {
      title.textContent = m[1];
      title.insertAdjacentElement('afterend', node('span', 'bs-qd__badge', '−' + m[2].replace(/\s+/g, '\u00A0')));
    }
    return {
      el: el,
      min: parseInt(el.getAttribute('data-amount'), 10) || 1,
      ratio: parseFloat(el.getAttribute('data-price-ratio')) || 1,
    };
  });
  if (!items.length) return null;

  function bandFor(qty) {
    let active = items[0];
    items.forEach(function (it) {
      if (qty >= it.min) active = it;
    });
    return active;
  }

  function update(qty) {
    const active = bandFor(qty);
    items.forEach(function (it) {
      it.el.classList.toggle('is-active', it === active);
      // nativní zvýraznění prvního pásma z importu by se s naším tlouklo
      it.el.classList.remove('quantity-discounts__item--highlighted');
    });
    if (savedEl && isFinite(orig)) {
      const saved = Math.round(qty * orig * (1 - active.ratio) * 100) / 100;
      savedEl.textContent = formatPrice(saved);
      if (saveRow) saveRow.hidden = !(saved > 0);
    }
  }

  // klik/Enter na pásmu nastaví množství (jen když stepper existuje — vyprodaný
  // produkt ho nemá a pásma jsou pak jen informativní tabulka)
  if (amount) {
    items.forEach(function (it) {
      it.el.setAttribute('role', 'button');
      it.el.tabIndex = 0;
      const pick = function () {
        amount.value = String(it.min);
        amount.dispatchEvent(new Event('input', { bubbles: true }));
        amount.dispatchEvent(new Event('change', { bubbles: true }));
      };
      it.el.addEventListener('click', pick);
      it.el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pick();
        }
      });
    });
  } else {
    box.classList.add('bs-qd--static');
  }

  update(amount ? parseInt(amount.value, 10) || 1 : 1);
  return {
    box: box,
    update: update,
    ratioFor: function (qty) {
      return bandFor(qty).ratio;
    },
  };
}

// ---------------------------------------------------------------------------
// Vyprodaný produkt (#227, mock schválen 2026-08-31 — verze A): místo nákupní
// karty panel nedostupnosti (žádná časová osa ani slib doručení), hlídání
// dostupnosti jako primární akce, pod kartou blok alternativ ze souvisejících
// produktů skladem. Signál = Shoptet u neprodejného produktu NErenderuje
// `.add-to-cart-button` (ověřeno na SADPAL: form beze stepperu a CTA).
// ---------------------------------------------------------------------------
function buildAlternatives() {
  const cards = [...document.querySelectorAll('.products-related .p[data-micro="product"]')];
  const items = [];
  cards.forEach(function (card) {
    if (items.length >= ALT_MAX) return;
    const name = text(card.querySelector('a.name'));
    const price = text(card.querySelector('.price-final'));
    const avail = card.querySelector('.availability');
    const inStock = !!avail && (avail.classList.contains('is-in-stock') || L.inStockRe.test(text(avail)));
    const img = card.querySelector('a.image img');
    const href = card.querySelector('a.name') ? card.querySelector('a.name').getAttribute('href') : '';
    if (!name || !price || !href || !inStock) return;
    items.push({ name: name, price: price, img: img ? img.getAttribute('src') : '', href: href });
  });
  if (!items.length) return null;

  const wrap = node('div', 'bs-alt');
  const head = node('div', 'bs-alt__head');
  head.appendChild(node('div', 'bs-alt__title', LABEL_ALT_TITLE));
  head.appendChild(node('div', 'bs-alt__sub', LABEL_ALT_SUB));
  wrap.appendChild(head);
  const grid = node('div', 'bs-alt__grid');
  items.forEach(function (it) {
    const a = link(it.href, 'bs-alt__card');
    const photo = node('span', 'bs-alt__img');
    if (it.img) {
      const img = node('img');
      img.src = it.img;
      img.alt = '';
      img.loading = 'lazy';
      photo.appendChild(img);
    } else {
      photo.appendChild(icon('photo'));
    }
    a.appendChild(photo);
    a.appendChild(node('span', 'bs-alt__name', it.name));
    const stock = node('span', 'bs-alt__stock');
    stock.appendChild(icon('check'));
    stock.appendChild(node('span', null, L.inStock));
    a.appendChild(stock);
    a.appendChild(node('b', 'bs-alt__price', it.price));
    grid.appendChild(a);
  });
  wrap.appendChild(grid);
  return wrap;
}

function buildSoldOutCard(form) {
  const cart = form.querySelector('.p-to-cart-block');
  const card = node('div', 'bs-buy bs-buy--soldout');
  if (cart) cart.insertAdjacentElement('beforebegin', card);
  else form.appendChild(card);

  // hlavičkový pás stavu
  const head = node('div', 'bs-soldout__head');
  const badge = node('span', 'bs-soldout__icon');
  badge.appendChild(icon('close'));
  head.appendChild(badge);
  const headTxt = node('div', 'bs-soldout__headtxt');
  headTxt.appendChild(node('strong', 'bs-soldout__title', LABEL_SOLDOUT_TITLE));
  const soldoutText = node('span', 'bs-soldout__text', LABEL_SOLDOUT_TEXT);
  headTxt.appendChild(soldoutText);
  head.appendChild(headTxt);
  card.appendChild(head);
  // „Nevíme, kdy bude znovu skladem" → termín naskladnění, když ho export
  // objednávek u dodavatelů nese (restockDate.js, přepínač `restockDate`)
  mountDetailRestock(soldoutText, shoptetData('product'));

  const body = node('div', 'bs-soldout__body');

  // cena před vyprodáním — nativní ceny se přesunou (zdroj pravdy zůstává),
  // přeškrtnutí a ztlumení dělá CSS přes .bs-soldout__price
  const prices = form.querySelector('.p-final-price-wrapper');
  if (prices) {
    const row = node('div', 'bs-soldout__price');
    prices.classList.add('bs-soldout__prices');
    row.appendChild(prices);
    row.appendChild(node('span', 'bs-soldout__price-note', LABEL_SOLDOUT_PRICE));
    body.appendChild(row);
  }

  // množstevní pásma zůstávají informativní (bez stepperu jsou jen tabulka)
  const qd = buildQtyDiscounts(form);
  if (qd) body.appendChild(qd.box);
  card.qd = qd;

  // CTA: hlídání = primární (pouští NATIVNÍ watchdog odkaz → colorbox modal),
  // dotaz = sekundární; oba odkazy v DOM jsou (utility řádek je bere taky)
  const watchLink = document.querySelector('.p-detail a.link-icon.watchdog, .p-detail a[href*="hlidat"]');
  const askLink = document.querySelector('.p-detail a.link-icon.chat, .p-detail a[href*=":dotaz"]');
  const ctaRow = node('div', 'bs-soldout__cta');
  if (watchLink) {
    const watchBtn = node('button', 'bs-soldout__watch');
    watchBtn.type = 'button';
    watchBtn.appendChild(icon('eye'));
    watchBtn.appendChild(node('span', null, LABEL_SOLDOUT_WATCH));
    watchBtn.addEventListener('click', function () {
      watchLink.click();
    });
    ctaRow.appendChild(watchBtn);
  }
  // se zapnutým chatem (supportChat.js) jde dotaz do chatu, jinak nativní formulář
  if (chatOn()) {
    const askBtn = node('button', 'bs-soldout__ask');
    askBtn.type = 'button';
    askBtn.appendChild(node('span', null, LC.ask));
    asChatTrigger(askBtn, 'soldout');
    ctaRow.appendChild(askBtn);
  } else if (askLink) {
    const askBtn = node('button', 'bs-soldout__ask');
    askBtn.type = 'button';
    askBtn.appendChild(node('span', null, LABEL_SOLDOUT_ASK));
    askBtn.addEventListener('click', function () {
      askLink.click();
    });
    ctaRow.appendChild(askBtn);
  }
  if (ctaRow.children.length) body.appendChild(ctaRow);
  if (watchLink) body.appendChild(node('div', 'bs-soldout__note', LABEL_SOLDOUT_NOTE));

  // Nativní řádky tabulky, které u vyprodaného zůstávaly nedotčené, protože
  // `buildTimeline()` (a s ní `fillShippingNote`) se tu vůbec nevolá — nález
  // Tomáše 2026-08-31: „Položka byla vyprodána…" a holý odkaz „Možnosti
  // doručení" visely pod perexem bez jediného stylu. Termín pryč (stav nese
  // hlavička panelu), odkaz na možnosti se mění v tentýž chip jako u skladové
  // karty. Řádky si zapamatovat PŘED přesunem uzlu (pak `closest` nic nenajde).
  const table = form.querySelector('table.detail-parameters');
  const delivery = table && table.querySelector('.delivery-time');
  const shippingLink = table && table.querySelector('a.shipping-options');
  const consumed = [delivery, shippingLink].map(function (el) {
    return el ? el.closest('tr') : null;
  });
  if (shippingLink) {
    shippingLink.classList.add('bs-buy__note-link');
    if (!shippingLink.querySelector('.bs-icon')) {
      shippingLink.insertBefore(icon('truck'), shippingLink.firstChild);
    }
    const ship = node('div', 'bs-soldout__ship');
    ship.appendChild(shippingLink);
    body.appendChild(ship);
  }
  consumed.forEach(function (row) {
    if (row && row.isConnected) row.remove();
  });

  card.appendChild(body);

  // blok alternativ pod kartou (sourozenec — na mobilu jde pod panel)
  const alt = buildAlternatives();
  if (alt) card.insertAdjacentElement('afterend', alt);

  // mobilní lišta: místo sticky ATC (stickyAtc se bez CTA nestaví) stav + hlídání
  if (watchLink && !document.querySelector('.bs-soldout-bar')) {
    const bar = node('div', 'bs-soldout-bar');
    const info = node('div', 'bs-soldout-bar__info');
    info.appendChild(node('span', 'bs-soldout-bar__state', LABEL_SOLDOUT_STATE));
    const priceText = visiblePriceText(form.querySelector('.price-final')).replace(/\s+/g, ' ').trim();
    if (priceText) info.appendChild(node('span', 'bs-soldout-bar__price', priceText));
    bar.appendChild(info);
    const btn = node('button', 'bs-soldout-bar__watch');
    btn.type = 'button';
    btn.appendChild(icon('eye'));
    btn.appendChild(node('span', null, LABEL_SOLDOUT_WATCH));
    btn.addEventListener('click', function () {
      watchLink.click();
    });
    bar.appendChild(btn);
    document.body.appendChild(bar);
    document.body.classList.add('has-soldout-bar');
  }

  return card;
}

function buildBuyCard(form) {
  const cart = form.querySelector('.p-to-cart-block');
  if (!cart) return null;
  // Vyprodaný produkt: Shoptet nerenderuje CTA do košíku → panel nedostupnosti
  // místo nákupní karty (#227) — bez časové osy, upsellu i countdownu.
  if (!form.querySelector('.add-to-cart-button')) return buildSoldOutCard(form);
  const table = form.querySelector('table.detail-parameters');

  const card = node('div', 'bs-buy');
  cart.insertAdjacentElement('beforebegin', card);

  // 1. cena + časová osa
  const top = node('div', 'bs-buy__top');
  const priceCol = node('div', 'bs-buy__price');
  const prices = form.querySelector('.p-final-price-wrapper');
  if (prices) {
    prices.classList.add('bs-buy__prices');
    priceCol.appendChild(prices);
    // žlutý badge „−34 % · ušetříte 891 Kč" pod cenou (plní se živě z watch)
    const save = node('span', 'bs-buy__save');
    save.hidden = true;
    const additional = prices.querySelector('.price-additional');
    if (additional) additional.insertAdjacentElement('beforebegin', save);
    else prices.appendChild(save);
    card.saveBadge = save;
    // konec akční ceny pod badge („Akční cena platí do soboty 5. 9." / odpočet) —
    // slot se plní až z indexu (initProductDetail) a kreslí jen při reálné slevě
    const sale = node('span', 'bs-buy__sale-slot');
    sale.hidden = true;
    save.insertAdjacentElement('afterend', sale);
    card.saleSlot = sale;
  }
  top.appendChild(priceCol);
  card.priceCol = priceCol;
  paintPriceTag(card);
  const div = node('span', 'bs-buy__div');
  div.setAttribute('aria-hidden', 'true');
  top.appendChild(div);
  const timeline = buildTimeline(form, table);
  top.appendChild(timeline.el);
  card.appendChild(top);

  // množstevní pásma mezi cenou a CTA (#228) — klik nastavuje stepper
  const qd = buildQtyDiscounts(form);
  if (qd) card.appendChild(qd.box);
  card.qd = qd;

  // 2. řádek dopravy s tlačítkem „Možnosti doručení" (handover §4)
  card.appendChild(timeline.note);

  // 3. pás CTA: stepper | (tlačítko + text o servisu pod ním)
  const cta = node('div', 'bs-buy__cta');
  const row = node('div', 'bs-buy__row');
  const qty = form.querySelector('.add-to-cart');
  const btn = form.querySelector('.add-to-cart-button');
  if (qty) row.appendChild(qty);
  const ctaCol = node('div', 'bs-buy__cta-col');
  if (btn) ctaCol.appendChild(btn);
  // odkaz, ne tlačítko: bez JS/fetch vede na stránku servisu sám, s ním otevírá
  // modal (serviceModal.js chytá `a[data-bs-service]` v capture fázi)
  const warranty = node('a', 'bs-buy__warranty');
  warranty.href = serviceHref();
  warranty.dataset.bsService = '1';
  warranty.appendChild(icon('wrench'));
  const warrantyText = node('span', 'bs-buy__warranty-txt');
  warrantyText.appendChild(node('b', null, LABEL_WARRANTY_STRONG));
  warrantyText.appendChild(document.createTextNode(LABEL_WARRANTY_SEP));
  warrantyText.appendChild(node('span', 'bs-buy__warranty-more', LABEL_WARRANTY_REST));
  warranty.appendChild(warrantyText);
  ctaCol.appendChild(warranty);
  row.appendChild(ctaCol);
  cta.appendChild(row);
  card.appendChild(cta);

  // Vstup do chatu hned pod CTA (Tomáš 2026-10-03, varianta C): na mobilu je karta
  // poradce až pod doplňky a plovoucí kolečko se tam nekreslí. Jen mobil (CSS),
  // desktop má kolečko; jeden řádek, nikdy zalomený. Odkaz se záložním mailto:.
  if (chatOn()) {
    const ask = link(chatFallbackHref(), 'bs-buy__chat');
    ask.appendChild(icon('chat'));
    const txt = node('span', 'bs-buy__chat-txt', LC.buyboxLead + ' ');
    txt.appendChild(node('strong', null, LC.buyboxCta));
    ask.appendChild(txt);
    asChatTrigger(ask, 'buybox');
    card.appendChild(ask);
  }

  // 3. upsell ze souvisejících produktů; cenový strop = cena produktu (ta už
  // se přestěhovala z formuláře do `priceCol`)
  const upsell = buildUpsell(parseNumber(visiblePriceText(priceCol.querySelector('.price-final'))));
  if (upsell) card.appendChild(upsell.el);
  card.upsell = upsell;

  // zbylé prázdné řádky nativní tabulky pryč; kód si vezme utility řádek
  if (table) {
    table.querySelectorAll('tr').forEach(function (tr) {
      if (!tr.classList.contains('p-code') && !text(tr)) tr.remove();
    });
  }
  mountCampaignBanner(card);
  return card;
}

// Velkoobchodní cena proti běžné (wholesalePrice.js, schváleno 2026-09-13):
// přihlášený zákazník s VO ceníkem vidí nad cenou „Velkoobchodní cena · Jipos
// Premium", vedle ní přeškrtnutou běžnou cenu a v badge úspory (tmavá varianta,
// žlutá patří Slevoakci) „−20 % z běžné ceny · ušetříte 578 Kč". U výprodeje,
// kde se sleva ceníku neuplatnila, akci a vysvětlení (`paintSaleNote`). Běžná cena jde
// z indexu (`/specs` → `prices`), VO cena z `.price-final` na stránce. Kreslí
// render CTA (`paintWholesale`), tady se jen dotáhnou ceny do `card.wholesale`.
// Variantní produkt se přeskakuje: cena se mění s variantou a kód je master.
function mountWholesale(watch, card) {
  if (!card || !card.saveBadge) return;
  const ctx = wholesaleCustomer(shoptetData('customer'));
  const product = shoptetData('product');
  if (!ctx || !product || !product.code || product.hasVariants === true) return;
  pricesFor([product.code]).then(function (map) {
    const prices = map.get(product.code);
    if (!prices) return;
    card.wholesale = { ctx: ctx, prices: prices };
    watch.refresh();
  });
}

// Slot nad cenou má jednu prioritu (plán kampaní 6. 10.): popisek ceníku VO
// (paintWholesale) > „Výprodej skladu" (příznak výprodeje na produktu) >
// název kampaně u zboží akční nabídky („Říjnový výprodej"). Vlaječky čte
// z hlavní fotky (`.p-image` — přesunuté i nativní), ne ze stránky: řady
// Souvisejících pod detailem nesou vlastní vlaječky.
function mainFlags() {
  const image = document.querySelector('.p-image');
  return image ? Array.prototype.slice.call(image.querySelectorAll('.flag')) : [];
}

function priceTag() {
  const flags = mainFlags();
  if (flags.some(isClearanceFlag)) return { mod: 'clearance', text: LCAMP.clearancePrice };
  const campaign = flags.some(isActionFlag) ? activeCampaign() : null;
  return campaign ? { mod: 'campaign', text: campaign.t.priceTag || campaign.name } : null;
}

// Banner pod nákupní kartou (Black Friday, mock M15): zboží bez slevy odkáže na
// deštník. Kampaň bez textu banneru (Říjnový výprodej) nic nekreslí, zboží
// akční nabídky ani výprodeje taky (slevu nese popisek nad cenou) a přihlášený
// velkoobchod ne: jeho ceník akční ceny nemá, slevy deštníku pro něj neplatí.
function mountCampaignBanner(card) {
  const campaign = activeCampaign();
  if (!campaign || !campaign.t.buyBannerTitle || card.nextElementSibling && card.nextElementSibling.classList.contains('bs-buy-camp')) return;
  if (mainFlags().some((f) => isActionFlag(f) || isClearanceFlag(f))) return;
  if (wholesaleCustomer(shoptetData('customer'))) return;
  const banner = link(campaign.href, 'bs-buy-camp');
  banner.dataset.bsCampaign = campaign.id;
  const txt = node('span', 'bs-buy-camp__txt');
  txt.appendChild(node('b', 'bs-buy-camp__title', campaign.t.buyBannerTitle));
  txt.appendChild(node('span', 'bs-buy-camp__body', campaign.t.buyBannerText(campaign.claim)));
  banner.appendChild(icon('percent', 'bs-buy-camp__ic'));
  banner.appendChild(txt);
  const cta = node('span', 'bs-buy-camp__cta', campaign.t.buyBannerCta);
  cta.appendChild(icon('arrow-right'));
  banner.appendChild(cta);
  card.insertAdjacentElement('afterend', banner);
  trackPromo(banner, campaign, 'buy_banner');
}

function paintPriceTag(card) {
  const prices = card.priceCol && card.priceCol.querySelector('.bs-buy__prices');
  if (!prices) return;
  const tag = prices.querySelector(':scope > .bs-buy__vo-label') ? null : priceTag();
  let el = prices.querySelector(':scope > .bs-buy__tag');
  if (!tag) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = node('span', 'bs-buy__tag');
    prices.insertBefore(el, prices.firstChild);
  }
  // zápis jen při změně: cenu hlídá observer i na atributech (varianty), stejná
  // hodnota by vyrobila záznam mutace a nový render
  const cls = 'bs-buy__tag bs-buy__tag--' + tag.mod;
  if (el.className !== cls) el.className = cls;
  if (el.textContent !== tag.text) el.textContent = tag.text;
}

// Popisek nad cenou + přeškrtnutá běžná cena; `adv` null = oboje pryč.
// Uzly visí v `.bs-buy__prices` (řádek cen se skládá wrapem, viz SCSS).
// Výprodej (`sale_blocked`, mock 2026-09-14): popisek „Výprodejová cena" a pod
// cenou i osou řádek „Na výprodejovou cenu se sleva ceníku … neuplatní." s
// odkazem na Moje slevy. Řádek je sourozenec sloupce ceny v `.bs-buy__top`
// (desktop: vlastní řádek přes celou šířku, mobil: hned pod cenou); nad
// nativním škrtnutím (`nativeStd`) zůstává jen on, popisek a škrtnutí ne.
function paintWholesale(card, adv, currency, nativeStd) {
  const prices = card.priceCol && card.priceCol.querySelector('.bs-buy__prices');
  if (!prices) return;
  const sale = !!adv && adv.state === 'sale_blocked';
  const priced = !!adv && !nativeStd;
  let label = prices.querySelector(':scope > .bs-buy__vo-label');
  let was = prices.querySelector(':scope > .bs-buy__vo-was');
  if (!priced) {
    if (label) label.remove();
    if (was) was.remove();
  } else {
    if (!label) {
      label = node('span', 'bs-buy__vo-label');
      prices.insertBefore(label, prices.firstChild);
    }
    label.textContent = sale ? VO.saleLabel : priceLabel(card.wholesale.ctx);
    if (!was) {
      was = node('s', 'bs-buy__vo-was');
      prices.appendChild(was);
    }
    was.textContent = formatPrice(adv.mo, currency);
  }
  paintSaleNote(card, sale);
}

function paintSaleNote(card, show) {
  const top = card.priceCol.parentNode;
  let note = top && top.querySelector(':scope > .bs-buy__vo-note');
  if (top) top.classList.toggle('bs-buy__top--note', show);
  if (!show) {
    if (note) note.remove();
    return;
  }
  if (note) return;
  note = node('p', 'bs-buy__vo-note');
  note.appendChild(icon('info'));
  const body = node('span', null, saleNoteText(card.wholesale.ctx) + ' ');
  body.appendChild(link(page('accountDiscounts'), null, VO.saleLink));
  note.appendChild(body);
  card.priceCol.insertAdjacentElement('afterend', note);
}

// CTA: ikona + popisek + cena (qty × cena + vybrané příslušenství). Form ani
// submit se nesahá; příslušenství jde do košíku AŽ po odeslání hlavního formu,
// přes `shoptet.cartShared.addToCart` (jediná povolená cesta).
function enhanceCta(form, watch, card) {
  const btn = form.querySelector('.add-to-cart-button');
  if (!btn || btn.querySelector('.bs-cta__label')) return;
  const label = node('span', 'bs-cta__label', text(btn));
  const price = node('span', 'bs-cta__price');
  btn.textContent = '';
  btn.appendChild(icon('cart'));
  btn.appendChild(label);
  btn.appendChild(price);

  const upsell = card && card.upsell;
  const save = card && card.saveBadge;
  const qd = card && card.qd;
  function render(state) {
    const extras = upsell ? upsell.sum() : 0;
    // množstevní pásmo (#228): CTA nese cenu, kterou košík opravdu spočítá
    // (quantityRatio) — bez toho by od 2. pásma výš lhalo o pár procent
    const ratio = qd ? qd.ratioFor(state.qty) : 1;
    if (qd) qd.update(state.qty);
    const total = isFinite(state.total) ? Math.round(state.total * ratio * 100) / 100 : NaN;
    price.textContent = isFinite(total) ? ' · ' + formatPrice(total + extras, state.currency) : '';
    document.dispatchEvent(new CustomEvent('bs:pd-qd', { detail: { ratio: ratio } }));
    if (save) {
      const std = state.standard;
      const unit = state.unit;
      const pct = pctOf(std, unit); // dolů jako nativní „–12 %"
      const discounted = pct !== null;
      // VO cena jen bez nativní přeškrtnuté ceny — rozhoduje její PŘÍTOMNOST
      // (`.price-standard`), ne procento: sleva pod 1 % (1 000 → 995) má badge
      // skrytý, ale škrtnutí Shoptetu na stránce je a druhé vedle něj nesmí
      // vzniknout (Codex review #366). Stejné pravidlo jako karta výpisu.
      const nativeStd = isFinite(std);
      // `adv.state`: 'vo' = tmavý badge VO výhody, 'sale_blocked' = výprodej,
      // sleva ceníku se neuplatnila → žlutý badge akce (stejná informace jako
      // u nepřihlášeného) + řádek s vysvětlením
      const adv = card.wholesale ? wholesaleAdvantage(card.wholesale.prices, unit) : null;
      const vo = !nativeStd && adv && adv.state === 'vo';
      if (discounted) {
        save.textContent = '−' + pct + ' % · ' + LABEL_SAVE + ' ' + formatPrice(std - unit, state.currency);
        save.hidden = false;
      } else if (adv && !nativeStd) {
        save.textContent = saveText(adv, state.currency);
        save.hidden = false;
      } else {
        save.hidden = true;
      }
      save.classList.toggle('bs-buy__save--vo', !!vo);
      if (card.wholesale) paintWholesale(card, adv, state.currency, nativeStd);
      paintPriceTag(card);
      // konec akce se ukáže jen při reálné slevě NA STRÁNCE (index může být pozadu)
      card.discounted = discounted;
      // Akční cena bez přeškrtnutí: Shoptet škrtá jen pole „běžná cena", ne cenu
      // před akcí, takže výprodej zboží bez běžné ceny (polovina karet vlny 1,
      // 6. 10.) slevu na stránce neukáže. Omnibus (priceHistory.js) ji z historie
      // cen ukáže i tak — třída `price-action` je značka platformy.
      card.priceAction = !discounted && !!card.priceCol.querySelector('.price-final.price-action');
      if (card.saleSlot) card.saleSlot.hidden = !(discounted && card.saleSlot.firstChild);
    }
    // sticky ATC lišta (stickyAtc.js) si příplatek přičte sama
    document.dispatchEvent(new CustomEvent('bs:pd-extras', { detail: { sum: extras } }));
  }
  watch.subscribe(render);
  if (upsell) {
    upsell.onChange(function () {
      render(watch.state);
    });
    // Guard dvojího odeslání: formulář umí odeslat CTA i sticky ATC lišta a
    // dvojklik na tlačítko taky vyrobí dva submity — bez pojistky by se
    // příslušenství přidalo dvakrát. Blokujeme po dobu, než doběhnou naplánované
    // `addToCart` volání; prázdný výběr nic neplánuje, takže ani neblokuje.
    let pendingUntil = 0;
    form.addEventListener('submit', function () {
      if (Date.now() < pendingUntil) return;
      const picked = upsell.selected();
      if (!picked.length) return;
      pendingUntil = Date.now() + ADD_GAP_MS * (picked.length + 1);
      picked.forEach(function (it, i) {
        setTimeout(function () {
          addToCart({ productCode: it.code, amount: 1 });
        }, ADD_GAP_MS * (i + 1));
      });
    });
  }
}

// ---------------------------------------------------------------------------
// Poradce + utility řádek
// ---------------------------------------------------------------------------

// Nativní „Zeptat se" (`a.link-icon.chat`) otevírá colorbox formulář
// „Dotaz na prodejce" přes delegovaný handler default JS — vázaný na nativní
// prvek, ne na href. Náš CTA sdílel jen href, takže holý klik navigoval na
// fragment `:dotaz/` bez šablony (nález dne D 2026-09-11, ostrá i dev).
// Proxy klik na nativní odkaz jako u hlídacího psa v soldout panelu; href
// zůstává (Ctrl/Cmd/prostřední tlačítko, bez JS, odpojený nativní uzel).
function proxyNativeClick(cta, native) {
  cta.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!native || !native.isConnected) return;
    e.preventDefault();
    native.click();
  });
}

function buildAdvisor(data) {
  const ask = data.querySelector('.link-icons a.chat');
  const card = node('div', 'bs-advisor');

  const photo = node('span', 'bs-advisor__photo');
  fillAvatar(photo, 'advisor', 44);
  card.appendChild(photo);

  const body = node('div', 'bs-advisor__body');
  body.appendChild(node('strong', 'bs-advisor__title', LABEL_ADVISOR));
  const meta = node('div', 'bs-advisor__meta');
  meta.appendChild(link('mailto:' + CONTACT.email, 'bs-advisor__mail', CONTACT.email));
  meta.appendChild(node('span', null, ' · ' + CONTACT.hours));
  body.appendChild(meta);
  card.appendChild(body);

  // Se zapnutým chatem (supportChat.js) otevírá CTA chat; nativní formulář
  // „Dotaz na prodejce" zůstává v utility řádku pod kartou („Zeptat se").
  if (chatOn()) {
    // plné tlačítko (Tomáš 2026-10-03, varianta B): obrysové na mobilu zapadlo
    const cta = node('button', 'bs-advisor__btn bs-advisor__btn--ink bs-advisor__btn--chat');
    cta.type = 'button';
    cta.appendChild(icon('chat'));
    cta.appendChild(node('span', null, LC.cta));
    asChatTrigger(cta, 'detail');
    card.appendChild(cta);
  } else if (ask) {
    const cta = link(ask.getAttribute('href'), 'bs-advisor__btn bs-advisor__btn--ink');
    cta.appendChild(icon('headset'));
    cta.appendChild(node('span', null, LABEL_ADVISOR_CTA));
    cta.setAttribute('rel', 'nofollow');
    proxyNativeClick(cta, ask);
    card.appendChild(cta);
  }
  // Návod = hlavní akce karty (zadání Tomáše 2026-09-05: červené pozadí, bílý
  // text i ikona), Poradna zůstává obrysová
  if (document.getElementById('relatedFiles')) {
    const manual = link('#relatedFiles', 'bs-advisor__btn bs-advisor__btn--primary');
    manual.appendChild(icon('book'));
    manual.appendChild(node('span', null, LABEL_MANUAL));
    card.appendChild(manual);
  }

  // mobil: volat je smysluplnější než odkaz na formulář (m05-product.md §6);
  // bez telefonu (phonesOn) kolečko nevzniká a CSS nechá na mobilu tlačítko
  // Poradna (`html.bs-no-phone`)
  if (phonesOn()) {
    const call = link(telHref(CONTACT.phone), 'bs-advisor__call');
    call.setAttribute('aria-label', L.call(CONTACT.phone));
    call.appendChild(icon('phone'));
    card.appendChild(call);
  }

  return card;
}

function buildMeta(data) {
  const row = node('div', 'bs-meta');

  const icons = data.querySelector('.link-icons');
  if (icons) {
    icons.classList.add('bs-meta__links');
    icons.querySelectorAll('a.link-icon').forEach(function (a) {
      if (a.classList.contains('print')) return; // prototyp tisk nemá
      const glyph = a.classList.contains('watchdog') ? 'heart' : a.classList.contains('chat') ? 'chat' : 'file';
      a.insertAdjacentElement('afterbegin', icon(glyph));
    });
    row.appendChild(icons);
  }

  row.appendChild(node('span', 'bs-meta__spacer'));

  const brandLink = data.querySelector('.p-detail-inner-header .brand-wrapper a');
  if (brandLink) {
    const brand = node('span', 'bs-meta__brand');
    brand.appendChild(node('span', 'bs-meta__label', LABEL_BRAND + ' '));
    brand.appendChild(link(brandLink.getAttribute('href'), null, text(brandLink)));
    row.appendChild(brand);
  }

  const table = data.querySelector('#product-detail-form table.detail-parameters');
  const code = table && table.querySelector('tr.p-code td span');
  if (code) {
    const wrap = node('span', 'bs-meta__code');
    wrap.appendChild(node('span', 'bs-meta__label', LABEL_CODE + ' '));
    wrap.appendChild(code);
    row.appendChild(wrap);
    const codeRow = table.querySelector('tr.p-code');
    if (codeRow) codeRow.remove();
  }
  if (table && !table.querySelector('tr')) table.remove();

  // prázdné nativní obaly (sociální ikony po přesunu, Cofidis, Hledejceny) by
  // ve flex sloupci s `gap` držely každý 14 px mezery
  data.querySelectorAll(':scope > .buttons-wrapper').forEach(function (wrap) {
    if (!wrap.children.length || !text(wrap)) wrap.remove();
  });

  return row;
}

// ---------------------------------------------------------------------------
// Sekce (nativní taby / mobilní akordeon → obsah pod sebou)
// ---------------------------------------------------------------------------

function sectionLabel(id) {
  const tab = document.querySelector('.shp-tab-link[href="#' + id + '"], .shp-accordion-link[href="#' + id + '"]');
  const found = ANCHORS.filter(function (a) {
    return a.id === id;
  })[0];
  return text(tab) || (found ? found.label : '');
}

function openSections() {
  document.querySelectorAll('.p-detail-tabs-wrapper .tab-pane').forEach(function (pane) {
    pane.classList.add('bs-pd-sec');
    if (!pane.id) return;
    const heading = pane.querySelector('h1, h2');
    if (heading) {
      heading.classList.add('bs-pd-sec__title');
      return;
    }
    const label = sectionLabel(pane.id);
    if (label) pane.insertAdjacentElement('afterbegin', node('h2', 'bs-pd-sec__title', label));
  });

  buildDescription();
  buildRelatedFiles();
  buildRatingPanel();
  buildDiscussion();
  pairEmptySections();
  buildBrand();
}

// Produkt bez hodnocení I bez diskuze: obě sekce vedle sebe v jedné mřížce
// (review Tomáše 2026-09-09 — dvě prázdné sekce pod sebou s tlačítky vpravo
// mátly). Panely musí být sourozenci (Shoptet je tak renderuje); scroll-spy
// i kotvy jedou po id, obal je nezajímá.
function pairEmptySections() {
  const rate = document.getElementById('ratingTab');
  const disc = document.getElementById('productDiscussion');
  if (!rate || !disc || rate.parentNode !== disc.parentNode) return;
  if (!rate.classList.contains('bs-pd-rate--empty') || !disc.classList.contains('bs-pd-disc--empty')) return;
  const duo = node('div', 'bs-pd-duo');
  rate.parentNode.insertBefore(duo, rate);
  duo.appendChild(rate);
  duo.appendChild(disc);
}

// R4 auditu K (2026-09-05, rozhodnutí Tomáše): generátor popisů nesází NBSP nikde,
// takže „3 950 W" nebo „15 960 l/h" se v pásu klíčových parametrů a v tabulce
// parametrů na mobilu lámaly mezi tisíci nebo mezi číslem a jednotkou. Plošný
// `white-space: nowrap` nejde — tabulka nese 1 081 hodnot nad 30 znaků (max 119)
// a pás má na mobilu 3 buňky po ~104 px s hodnotami do 22 znaků. Za běhu se proto
// nahradí JEN mezera uvnitř čísla (tisíce) a mezi číslem a jednotkou za NBSP;
// zbytek hodnoty se láme dál. 2 424 popisů zůstává beze změny (žádný re-apply).
const NUMERIC_UNITS = L.numericUnits;
const NUMERIC_BREAK_RE = new RegExp(
  '(\\d) (?=\\d{3}(?!\\d)|(?:' + NUMERIC_UNITS + ')(?![\\p{L}\\d]))',
  'gu'
);
function fixNumericBreaks(root) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    const v = node.nodeValue;
    if (!v || !/\d /.test(v)) continue;
    const next = v.replace(NUMERIC_BREAK_RE, '$1\u00a0');
    if (next !== v) node.nodeValue = next;
  }
}

// Video pásu vizuálů (§5 šablony popisu): přehrát jen ve viewportu, jinak pauza.
// Idempotentní (re-init po AJAXu značí video `data-bs-io`); bez
// IntersectionObserveru se přehraje rovnou jako dřív.
function initMediaVideos(pane) {
  const vids = Array.from(pane.querySelectorAll('.bs-pc-media video')).filter(function (v) { return !v.dataset.bsIo; });
  if (!vids.length) return;
  const play = function (v) { const p = v.play(); if (p && p.catch) p.catch(function () {}); };
  vids.forEach(function (video) {
    video.dataset.bsIo = '1';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
  });
  if (!('IntersectionObserver' in window)) { vids.forEach(play); return; }
  const io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) play(e.target); else e.target.pause(); });
  }, { rootMargin: '200px 0px' });
  vids.forEach(function (v) { io.observe(v); });
}

// Popis: prose vrstva; FAQ z popisu + nativní doplňkové parametry vedle sebe
// (prototyp §11 „Poradna + Doplňkové parametry", grid 1fr 1fr)
function buildDescription() {
  const pane = document.getElementById('description');
  if (!pane) return;
  const desc = pane.querySelector('.description-inner');
  if (desc) {
    desc.classList.add('bs-pd-desc');
    // bloky šablony si sazbu nesou samy; prózová typografie jen pro starý popis.
    // Shoptet popis obaluje do `.basic-description` (živý DOM), fixture i vstřik
    // harnessu ho nemusí mít — proto hledání potomků, ne přímých dětí.
    const hasBlocks = !!desc.querySelector('[class^="bs-pc-"]');
    desc.classList.add(hasBlocks ? 'bs-pd-desc--blocks' : 'bs-pd-desc--prose');
    // R4: tisíce a číslo–jednotka nezalomitelné v pásu klíčových parametrů,
    // chipech a tabulce parametrů (text jinak zůstává zalomitelný)
    desc.querySelectorAll('.bs-pc-keyspec > strong, .bs-pc-keyspec > b, .bs-pc-chip, .bs-pc-specs__table td').forEach(fixNumericBreaks);
  }
  // Video v mediálním bloku šablony: smyčka bez zvuku, ale přehrání až když je
  // pás vidět. Markup od 2026-09-12 nese `preload="none"` + poster a ŽÁDNÝ
  // `autoplay` (přebíjí preload a prohlížeč stáhl celý klip při načtení detailu;
  // 705 produktů na ostré, epic #311) — `play()` z IntersectionObserveru teprve
  // spustí stahování. Starší markup s `autoplay` (do re-importu) tudy projde
  // taky: stáhl se už při parsování, tady jen pauzuje mimo viewport. Pojistka
  // atributů zůstává (prohlížeče, které je z markupu ignorují — handover §Interakce).
  initMediaVideos(pane);
  let params = pane.querySelector('.extended-description');
  const faq = desc ? desc.querySelector('.bs-pc-faq') : null;
  const specs = desc ? desc.querySelector('.bs-pc-specs__table') : null;
  // dvě tabulky parametrů → jedna karta s přepínačem (review Tomáše 2026-09-09)
  if (params && specs && unifySpecs(specs, params)) params = null;
  if (params) {
    params.classList.add('bs-pd-params');
    if (!document.getElementById('parametry')) params.id = 'parametry';
  }
  if (!faq && !params) return;
  const qa = node('div', 'bs-pd-qa' + (faq && params ? '' : ' bs-pd-qa--single'));
  if (faq) {
    buildExpert(faq);
    qa.appendChild(faq);
  }
  if (params) qa.appendChild(params);
  pane.appendChild(qa);
}

// Klíč řádku parametrů pro dedupe = `specKeyOf` (textKey.js): bez dvojtečky,
// diakritiky a velikosti písmen („Hmotnost:" = „hmotnost"), významové symboly
// (Ø, °, %…) zůstávají; název bez písmen a číslic = null, neslučuje se
function specKey(th) {
  return specKeyOf(text(th));
}

// Sjednocení „Technické parametry" (tabulka z GENEROVANÉHO popisu — hodnoty
// vytažené z prózy produktu, kurátorský výběr) a nativních „Doplňkových
// parametrů" (popisné parametry Shoptetu z administrace/importu, včetně
// Kategorie · Hmotnost · EAN) do JEDNÉ karty se dvěma záložkami (review
// Tomáše 2026-09-09: dvě skoro stejné tabulky pod sebou mátly a některé
// technické hodnoty ležely až v doplňkových). Pravidla:
//   • řádek doplňkových se STEJNÝM názvem jako technický se schová — technická
//     vrstva má přednost (přesná hodnota z textu vs. import), v DOM zůstává;
//   • když po dedupe žádný doplňkový řádek nezbude, karta záložky nemá;
//   • nativní obal `.extended-description` (s nadpisem) z toku mizí — tabulka
//     se STĚHUJE (ne klonuje), Shoptet ji nikde jinde nečte;
//   • kotva `#parametry` zůstává na kartě (šablona popisu ji nese sama).
function unifySpecs(specs, params) {
  const techTable = specs.querySelector('table');
  const nativeTable = params.querySelector('table.detail-parameters');
  if (!techTable || !nativeTable) return false;
  const known = {};
  techTable.querySelectorAll('tr').forEach(function (tr) {
    const th = tr.querySelector('th');
    const key = th && specKey(th);
    if (key) known[key] = true;
  });
  let extra = 0;
  nativeTable.querySelectorAll('tr').forEach(function (tr) {
    const th = tr.querySelector('th');
    if (!th) return;
    const key = specKey(th);
    if (key && known[key]) {
      tr.hidden = true;
      tr.classList.add('bs-pd-specs__dup');
    } else {
      extra += 1;
    }
  });
  const tech = techTable.querySelectorAll('tr:not([hidden])').length;
  // R4 platí i pro nativní hodnoty („8.95 kg", „2 860 ot./min") — v jedné kartě
  // se lámou stejně jako technické
  nativeTable.querySelectorAll('td').forEach(fixNumericBreaks);
  params.remove();
  specs.classList.add('bs-pd-specs');
  if (!extra) return true;

  const h2 = specs.querySelector('h2');
  if (h2) h2.textContent = LABEL_SPECS_TITLE;

  const tabs = node('div', 'bs-pd-specs__tabs');
  tabs.setAttribute('role', 'tablist');
  const panels = [];
  const tabOf = function (label, count, table, key) {
    const panel = node('div', 'bs-pd-specs__panel bs-pd-specs__panel--' + key);
    panel.id = 'bs-pd-specs-' + key;
    panel.setAttribute('role', 'tabpanel');
    panel.appendChild(table);
    const btn = node('button', 'bs-pd-specs__tab bs-pd-specs__tab--' + key);
    btn.type = 'button';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-controls', panel.id);
    btn.appendChild(node('span', null, label));
    btn.appendChild(node('span', 'bs-pd-specs__count', String(count)));
    tabs.appendChild(btn);
    panels.push({ btn: btn, panel: panel });
    return panel;
  };
  const techPanel = tabOf(LABEL_SPECS_TECH, tech, techTable, 'tech');
  const extraPanel = tabOf(LABEL_SPECS_EXTRA, extra, nativeTable, 'extra');
  const select = function (which) {
    panels.forEach(function (p) {
      const on = p === which;
      p.btn.classList.toggle('is-active', on);
      p.btn.setAttribute('aria-selected', on ? 'true' : 'false');
      p.btn.tabIndex = on ? 0 : -1;
      p.panel.hidden = !on;
    });
  };
  panels.forEach(function (p) {
    p.btn.addEventListener('click', function () {
      select(p);
    });
  });
  tabs.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const i = panels.findIndex(function (p) {
      return p.btn.classList.contains('is-active');
    });
    const next = panels[(i + (e.key === 'ArrowRight' ? 1 : panels.length - 1)) % panels.length];
    select(next);
    next.btn.focus();
    e.preventDefault();
  });
  if (h2) h2.insertAdjacentElement('afterend', tabs);
  else specs.insertBefore(tabs, specs.firstChild);
  tabs.insertAdjacentElement('afterend', techPanel);
  techPanel.insertAdjacentElement('afterend', extraPanel);
  select(panels[0]);
  return true;
}

// Hlavička poradny: kdo na otázky odpovídá (zadání Tomáše 2026-09-04). Sedí mezi
// nadpisem sekce a seznamem otázek, takže se kreslí JEN u produktů s FAQ blokem
// v popisu — u ostatních by slibovala odpovědi, které na stránce nejsou.
// Fotka žije mimo `dist/` (deploy/upload-media.ps1); když se nenačte, zmizí jen
// ona a text karty zůstane.
function buildExpert(faq) {
  if (faq.querySelector('.bs-pd-expert')) return;
  const card = node('div', 'bs-pd-expert');

  const fig = node('span', 'bs-pd-expert__photo');
  const img = document.createElement('img');
  img.src = MEDIA + EXPERT.photo;
  img.alt = EXPERT.name;
  img.width = 720;
  img.height = 900;
  img.loading = 'lazy';
  img.decoding = 'async';
  img.addEventListener('error', function () {
    fig.remove();
  });
  fig.appendChild(img);
  card.appendChild(fig);

  const body = node('div', 'bs-pd-expert__body');
  body.appendChild(node('strong', 'bs-pd-expert__title', LABEL_EXPERT));
  body.appendChild(node('div', 'bs-pd-expert__name', EXPERT.name));
  body.appendChild(node('div', 'bs-pd-expert__role', EXPERT.role));

  const rows = node('div', 'bs-pd-expert__rows');
  const mail = link('mailto:' + CONTACT.email, 'bs-pd-expert__row');
  mail.appendChild(icon('mail'));
  mail.appendChild(node('span', null, CONTACT.email));
  rows.appendChild(mail);
  if (phonesOn()) {
    const tel = link(telHref(CONTACT.phone), 'bs-pd-expert__row');
    tel.appendChild(icon('phone'));
    tel.appendChild(node('span', null, CONTACT.phone));
    rows.appendChild(tel);
  }
  body.appendChild(rows);

  // CTA jede na NATIVNÍ odkaz „Zeptat se" (colorbox formulář Shoptetu); když
  // ho stránka nemá, tlačítko nevzniká — vlastní formulář nestavíme.
  const ask = document.querySelector('.p-detail a.link-icon.chat, .p-detail a[href*=":dotaz"]');
  if (ask) {
    const cta = link(ask.getAttribute('href'), 'bs-pd-expert__cta');
    cta.appendChild(icon('headset'));
    cta.appendChild(node('span', null, LABEL_EXPERT_CTA));
    cta.setAttribute('rel', 'nofollow');
    proxyNativeClick(cta, ask);
    body.appendChild(cta);
  }
  card.appendChild(body);

  const list = faq.querySelector('.bs-pc-faq__list');
  if (list) faq.insertBefore(card, list);
  else faq.appendChild(card);
}

// Související soubory: nativní `ul > li(img + a)` → karty odkazů
// Sekce návodu (zadání Tomáše 2026-09-05): nativní `ul#relatedFiles` se jmenuje
// „Související soubory (N)" a byl to nevýrazný seznam odkazů. Teď je to viditelná
// sekce: titulek podle obsahu („Návod k produktu" / „Návod a dokumenty ke
// stažení"), každý soubor = zvýrazněná karta s medailonkem, názvem, typem
// souboru a červeným CTA „Stáhnout návod". Nativní `<a>` zůstává (href,
// target, title — Shoptet je plní z administrace), jen se obalí; typ souboru se
// bere z `img[alt]` (pdf.gif) nebo z přípony v href, nic se neodhaduje.
function fileType(li, a) {
  const img = li.querySelector(':scope > img');
  const alt = img && (img.getAttribute('alt') || '').trim();
  if (alt && FILE_EXT_RE.test(alt)) return alt.toUpperCase();
  const m = (a.getAttribute('href') || '').match(/\.([a-z0-9]{2,4})(?:[?#]|$)/i);
  return m && FILE_EXT_RE.test(m[1]) ? m[1].toUpperCase() : '';
}

// druh položky: návod (kniha, „Stáhnout návod") · video (play, „Přehrát video")
// · soubor (dokument, „Stáhnout") · jiný odkaz (šipka, „Otevřít")
function fileKind(li, a) {
  const href = a.getAttribute('href') || '';
  if (VIDEO_RE.test(href)) return { glyph: 'play', cta: LABEL_FILE_CTA_VIDEO, type: LABEL_FILE_VIDEO, manual: false };
  const type = fileType(li, a);
  if (MANUAL_RE.test(text(a))) return { glyph: 'book', cta: LABEL_FILE_CTA_MANUAL, type: type, manual: true };
  if (type) return { glyph: 'file', cta: LABEL_FILE_CTA_DOC, type: type, manual: false };
  return { glyph: 'arrow-right', cta: LABEL_FILE_CTA_LINK, type: '', manual: false };
}

function buildRelatedFiles() {
  const list = document.getElementById('relatedFiles');
  if (!list) return;
  list.classList.add('bs-pd-files');
  const items = Array.prototype.slice.call(list.querySelectorAll(':scope > li')).filter(function (li) {
    return !!li.querySelector('a[href]');
  });
  const kinds = items.map(function (li) {
    return fileKind(li, li.querySelector('a[href]'));
  });
  const manuals = kinds.filter(function (k) {
    return k.manual;
  }).length;
  // návod první, zbytek v pořadí z administrace (sekce je o návodu; SADPAL
  // má před návodem video)
  items.forEach(function (li, i) {
    if (kinds[i].manual) list.appendChild(li);
  });
  items.forEach(function (li, i) {
    if (!kinds[i].manual) list.appendChild(li);
  });

  const title = list.querySelector('.bs-pd-sec__title');
  if (title) {
    title.textContent = '';
    title.classList.add('bs-pd-files__title');
    const ic = node('span', 'bs-pd-files__title-ic');
    ic.appendChild(icon('book'));
    title.appendChild(ic);
    const label = !items.length || manuals === items.length
      ? (items.length > 1 ? LABEL_FILES_MANUALS : LABEL_FILES_MANUAL)
      : (manuals ? LABEL_FILES_MIXED : LABEL_FILES_DOCS);
    title.appendChild(node('span', 'bs-pd-files__title-text', label));
    if (items.length > 1) title.appendChild(node('span', 'bs-pd-files__count', String(items.length)));
  }

  items.forEach(function (li, i) {
    li.classList.add('bs-pd-file');
    const a = li.querySelector('a[href]');
    if (a.querySelector('.bs-pd-file__ic')) return;
    const kind = kinds[i];
    const name = text(a);
    a.classList.add('bs-pd-file__link');
    a.textContent = '';

    const ic = node('span', 'bs-pd-file__ic');
    ic.appendChild(icon(kind.glyph));
    a.appendChild(ic);

    const body = node('span', 'bs-pd-file__body');
    body.appendChild(node('b', 'bs-pd-file__name', name));
    const meta = node('span', 'bs-pd-file__meta');
    if (kind.type) meta.appendChild(node('span', 'bs-pd-file__type', kind.type));
    if (a.getAttribute('target') === '_blank') meta.appendChild(node('span', null, LABEL_FILE_NEW_WINDOW));
    if (meta.childNodes.length) body.appendChild(meta);
    a.appendChild(body);

    const cta = node('span', 'bs-pd-file__cta');
    cta.appendChild(icon(kind.glyph === 'play' ? 'play' : kind.glyph === 'arrow-right' ? 'arrow-right' : 'download'));
    cta.appendChild(node('span', null, kind.cta));
    a.appendChild(cta);

    const img = li.querySelector(':scope > img');
    if (img) img.hidden = true;
  });
}

// „6.2.2026" → „6. 2. 2026" (typografie návrhu; nativní čas u diskuze „23.3.2026 20:12" nese i hodinu)
function czDate(str) {
  const m = String(str || '').match(/(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/);
  return m ? m[1] + '. ' + m[2] + '. ' + m[3] : String(str || '').trim();
}

// ---------------------------------------------------------------------------
// Hodnocení (design 2026-09-05, docs/design/2026-09-05-hodnoceni-diskuze —
// varianta A, rozhodnutí Tomáše): hlavička = titulek + počet + CTA, souhrn jako
// pás přes celou šířku (skóre | histogram), recenze jako karty ve 2 sloupcích,
// „Zobrazit další" jako obrysová pilulka. Nativní uzly se jen přesouvají;
// `#ratingWrapper` zůstává v DOM s formulářem, který default JS odkrývá přes
// `data-unveil` — trigger smí stát kdekoliv v dokumentu (sonda 2026-09-05:
// po přesunu dostane `#rate-form` třídu `visible` stejně).
// ---------------------------------------------------------------------------
function ratingOf(vote) {
  const v = vote.querySelector('[itemprop="ratingValue"]');
  const n = v ? parseInt(text(v), 10) : vote.querySelectorAll('.stars .star-on').length;
  return isFinite(n) ? n : 0;
}

// Skryje recenze pod prahem RATING_MIN_STARS (i ty, co dojdou přes loadMore);
// vrací počet viditelných.
function filterRatings(list) {
  let shown = 0;
  list.querySelectorAll(':scope > .vote-wrap').forEach(function (vote) {
    const low = ratingOf(vote) < RATING_MIN_STARS;
    vote.hidden = low;
    vote.classList.toggle('bs-pd-rate__low', low);
    if (!low) shown += 1;
  });
  return shown;
}

// „88 % zákazníků dává 4 a více hvězd" z nativního histogramu
// (`.rate-list[data-score]` + `.rate-count` „165x"); null bez dat.
function ratingShare(votes) {
  let total = 0;
  let top = 0;
  votes.querySelectorAll('.rate-list').forEach(function (row) {
    const score = parseInt(row.getAttribute('data-score'), 10);
    const m = text(row.querySelector('.rate-count')).match(/(\d+)/);
    const n = m ? parseInt(m[1], 10) : 0;
    total += n;
    if (score >= 4) top += n;
  });
  return total > 0 ? Math.round((top / total) * 100) : null;
}

function buildRatingPanel() {
  const tab = document.getElementById('ratingTab');
  const wrap = tab && tab.querySelector('#ratingWrapper');
  if (!wrap) return;
  tab.classList.add('bs-pd-rate');
  const count = ratingCount();

  // Sekce je grid (SCSS): titulek + počet | CTA v prvním řádku, zbytek přes
  // celou šířku; na mobilu jde CTA přes `order` až pod souhrn. Proto bez obalu
  // hlavičky — titulek i trigger jsou přímé děti sekce.
  const titleWrap = node('div', 'bs-pd-rate__title');
  const title = tab.querySelector(':scope > .bs-pd-sec__title');
  if (title) titleWrap.appendChild(title);
  if (count > 0) titleWrap.appendChild(node('span', 'bs-pd-rate__chip', String(count)));
  tab.insertBefore(titleWrap, tab.firstChild);
  let anchor = titleWrap;
  // prázdný stav (review Tomáše 2026-09-09): CTA stojí POD větou „Buďte
  // první…", ne vpravo v hlavičce; sekce se pak páruje s diskuzí vedle sebe
  // (pairEmptySections)
  const notice = tab.querySelector(':scope > p');
  const isEmpty = count === 0 && !!notice;
  if (isEmpty) tab.classList.add('bs-pd-rate--empty');
  const trigger = wrap.querySelector('.add-comment.rate-form-trigger');
  if (trigger) {
    const btn = trigger.querySelector('button, a');
    if (btn) {
      btn.textContent = '';
      btn.appendChild(icon('star-line'));
      btn.appendChild(node('span', null, LABEL_RATE_CTA));
    }
    trigger.classList.add('bs-pd-rate__cta');
    (isEmpty ? notice : titleWrap).insertAdjacentElement('afterend', trigger);
    anchor = trigger;
  }

  // souhrn: skóre + hvězdy + počet + podíl 4★+ | histogram
  const rateWrap = wrap.querySelector('.rate-wrap');
  const votes = wrap.querySelector('.votes');
  const avg = ratingAverage();
  if (avg > 0 && rateWrap) {
    const summary = node('div', 'bs-pd-rate__summary');
    summary.appendChild(rateWrap);
    const native = rateWrap.querySelector('.rate-star-wrap .stars.star-list');
    if (native) {
      native.insertAdjacentElement('beforebegin', stars(avg, 'bs-stars--lg bs-stars--panel'));
      native.hidden = true;
    }
    if (votes) {
      const share = ratingShare(votes);
      if (share !== null) rateWrap.appendChild(node('div', 'bs-pd-rate__share', share + '\u00a0% ' + LABEL_RATE_SHARE));
      // \u201e165x" \u2192 \u201e165" (n\u00e1vrh po\u010dty p\u00ed\u0161e hol\u00e9; hodnota z\u016fst\u00e1v\u00e1 v atributu
      // pro \u010dte\u010dku i harness)
      votes.querySelectorAll('.rate-count').forEach(function (c) {
        const m = text(c).match(/(\d+)/);
        if (m) {
          c.setAttribute('data-bs-count', m[1]);
          c.textContent = m[1];
        }
      });
      summary.appendChild(votes);
    }
    anchor.insertAdjacentElement('afterend', summary);
    anchor = summary;
  }
  wrap.classList.add('bs-pd-rate__formwrap');

  // seznam: filtr hvězd + prázdný stav. Nativní „Buďte první…" platí jen bez
  // hodnocení; vedle „89 hodnocení" (hvězdy bez textu) lhalo.
  if (notice) notice.classList.add('bs-pd-rate__notice');
  if (notice && count > 0) notice.hidden = true;
  const empty = node('p', 'bs-pd-rate__empty', LABEL_RATE_STARS_ONLY);
  empty.hidden = true;
  anchor.insertAdjacentElement('afterend', empty);

  const list = tab.querySelector('#ratingsList');
  const sync = function () {
    const shown = list ? filterRatings(list) : 0;
    empty.hidden = !(count > 0 && shown === 0);
    if (list) {
      list.querySelectorAll('.vote-time').forEach(function (t) {
        if (!t.dataset.bsCz) {
          t.dataset.bsCz = '1';
          t.textContent = czDate(text(t));
        }
      });
    }
  };
  if (list) {
    list.classList.add('bs-pd-rate__list');
    new MutationObserver(sync).observe(list, { childList: true });
  }
  sync();

  // „Zobrazit další": nativní handler po každém načtení SMAŽE `.listingControls`
  // a vloží nový z odpovědi (main-3g.js) → dekorovat znovu po
  // `ShoptetDOMProductRatingsLoaded`, ne jen jednou
  const decorateMore = function () {
    const more = tab.querySelector('.loadMore[data-context="ratings"]');
    if (!more || more.classList.contains('bs-pd-rate__more')) return;
    more.classList.add('bs-pd-rate__more');
    const btn = more.querySelector('button, a');
    if (btn) btn.textContent = LABEL_RATE_MORE;
  };
  decorateMore();
  document.addEventListener('ShoptetDOMProductRatingsLoaded', decorateMore);
}

// ---------------------------------------------------------------------------
// Diskuze (design 2026-09-05 — řádky varianty B + boční karta z A, rozhodnutí
// Tomáše): dotazy jako řádky oddělené linkou („?" dlaždice · otázka tučně ·
// meta jméno · datum · stav), odpověď obchodu odsazená s „J" dlaždicí,
// vpravo sticky karta „kdo odpovídá" s nativním triggerem formuláře.
// Nativní markup (SADPAL, 2026-09-05): #discussionsList > .vote-wrap
// (.vote-header > span > .vote-name · .vote-rating > .vote-title + .vote-time;
// .vote-content > [itemprop=text] + <br> + button.add-comment[data-id];
// vnořené odpovědi = .votes-wrap.discussion UVNITŘ .vote-wrap). „Odpovědět"
// přesouvá SDÍLENÝ #discussion-form do .vote-wrap (nastaví parentId) — post
// je proto grid, kde každé další dítě padá přes celou šířku.
// ---------------------------------------------------------------------------
// Blok „kdo odpovídá" (foto poradce + jméno + role) — boční karta diskuze
// i řádek pod CTA prázdného stavu; liší se jen velikostí fotky a popisky.
function discWho(mod, size, name, role) {
  const who = node('div', 'bs-pd-disc__who' + mod);
  const photo = node('span', 'bs-pd-disc__photo');
  fillAvatar(photo, 'advisor', size);
  who.appendChild(photo);
  const whoBody = node('div', 'bs-pd-disc__who-body');
  whoBody.appendChild(node('span', 'bs-pd-disc__name', name));
  whoBody.appendChild(node('span', 'bs-pd-disc__role', role));
  who.appendChild(whoBody);
  return who;
}

// nativní trigger formuláře dotazu: ikona + náš popisek, prvek zůstává nativní
function discCta(trigger) {
  const btn = trigger.querySelector('button, a');
  if (!btn) return;
  btn.textContent = '';
  btn.appendChild(icon('chats'));
  btn.appendChild(node('span', null, LABEL_DISC_CTA));
}

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(function (w) {
      return w.charAt(0).toUpperCase();
    })
    .join('');
}

// Autor příspěvku = naše odbornice? Zobrazujeme křestní jméno, ale administrace
// podepisuje odpovědi i plným jménem („Veronika Rambousková") — bereme obojí.
function isExpertName(name) {
  return name === EXPERT.name || String(name || '').indexOf(EXPERT.name + ' ') === 0;
}

function decoratePost(vote, list) {
  if (vote.classList.contains('bs-pd-disc__post')) return;
  const parentList = vote.parentElement;
  const isReply = parentList !== list;
  const name = text(vote.querySelector(':scope > .vote-header .vote-name'));
  const isShop = isReply && (SHOP_AUTHOR_RE.test(name) || isExpertName(name));
  vote.classList.add('bs-pd-disc__post');
  if (isReply) vote.classList.add('bs-pd-disc__post--reply');
  if (isShop) vote.classList.add('bs-pd-disc__post--shop');

  const tile = node('span', 'bs-pd-disc__tile', isReply ? (isShop ? LABEL_DISC_SHOP.charAt(0) : initials(name)) : '?');
  tile.setAttribute('aria-hidden', 'true');
  const body = node('div', 'bs-pd-disc__body');

  const content = vote.querySelector(':scope > .vote-content');
  const textEl = content && content.querySelector('[itemprop="text"]');
  const q = node('div', 'bs-pd-disc__q');
  if (textEl) q.appendChild(textEl);
  body.appendChild(q);

  const meta = node('div', 'bs-pd-disc__meta');
  const nameEl = vote.querySelector(':scope > .vote-header .vote-name');
  const titleEl = vote.querySelector(':scope > .vote-header .vote-title');
  const timeEl = vote.querySelector(':scope > .vote-header .vote-time');
  if (isShop) {
    meta.appendChild(node('b', 'bs-pd-disc__shop', LABEL_DISC_SHOP));
    meta.appendChild(node('span', 'bs-pd-disc__sep', ' · '));
  }
  if (nameEl) meta.appendChild(nameEl);
  if (titleEl && text(titleEl) && text(titleEl) !== text(textEl)) {
    meta.appendChild(node('span', 'bs-pd-disc__sep', ' · '));
    meta.appendChild(titleEl);
  }
  if (timeEl) {
    timeEl.textContent = czDate(text(timeEl));
    meta.appendChild(node('span', 'bs-pd-disc__sep', ' · '));
    meta.appendChild(timeEl);
  }
  const replies = vote.querySelector(':scope > .votes-wrap.discussion');
  const answered = !!(replies && replies.querySelector('.vote-wrap'));
  if (!isReply && !answered) {
    meta.appendChild(node('span', 'bs-pd-disc__sep', ' · '));
    meta.appendChild(node('span', 'bs-pd-disc__pending', LABEL_DISC_PENDING));
  }
  body.appendChild(meta);
  if (replies) body.appendChild(replies);
  if (content) body.appendChild(content); // zbylo tlačítko „Odpovědět" (+ formulář po kliku)

  const header = vote.querySelector(':scope > .vote-header');
  if (header) header.hidden = true; // prázdný obal po přesunu jména/času
  vote.insertBefore(body, vote.firstChild);
  vote.insertBefore(tile, body);
}

function buildDiscussion() {
  const tab = document.getElementById('productDiscussion');
  const container = tab && tab.querySelector('.discussionContainer');
  if (!container) return;
  tab.classList.add('bs-pd-disc');
  const wrapper = tab.querySelector('#discussionWrapper') || tab;

  // hlavička: titulek + počet dotazů
  const head = node('div', 'bs-pd-disc__head');
  const titleWrap = node('div', 'bs-pd-disc__title');
  const title = tab.querySelector('.bs-pd-sec__title');
  if (title) titleWrap.appendChild(title);
  const count = anchorCount('discussion');
  if (count) titleWrap.appendChild(node('span', 'bs-pd-disc__chip', String(count)));
  head.appendChild(titleWrap);
  wrapper.insertBefore(head, wrapper.firstChild);

  const notice = container.querySelector(':scope > p');
  if (notice) notice.classList.add('bs-pd-disc__notice');

  // mřížka: nativní kontejner (hláška, formulář, seznam) | boční karta
  const grid = node('div', 'bs-pd-disc__grid');
  const main = node('div', 'bs-pd-disc__main');
  grid.appendChild(main);
  head.insertAdjacentElement('afterend', grid);
  main.appendChild(container);

  // prázdný stav (review Tomáše 2026-09-09): bez boční karty — CTA zůstává
  // v nativním pořadí POD větou „Buďte první…", pod ním jen řádek, kdo
  // odpovídá; sekce se páruje s hodnocením vedle sebe (pairEmptySections)
  if (!count && notice) {
    tab.classList.add('bs-pd-disc--empty');
    const trigger = container.querySelector('.add-comment.discussion-form-trigger');
    if (trigger) {
      discCta(trigger);
      trigger.classList.add('bs-pd-disc__cta');
      notice.insertAdjacentElement('afterend', trigger);
      const who = discWho(' bs-pd-disc__who--inline', 32, LABEL_DISC_ANSWERS + ' ' + EXPERT.name, EXPERT.role);
      trigger.insertAdjacentElement('afterend', who);
    }
    return;
  }

  const side = node('aside', 'bs-pd-disc__side');
  grid.appendChild(side);

  side.appendChild(discWho('', 56, EXPERT.name, EXPERT.role + ' · ' + LABEL_DISC_SIDE_ROLE));
  side.appendChild(node('p', 'bs-pd-disc__side-text', LABEL_DISC_SIDE_TEXT));
  const trigger = container.querySelector('.add-comment.discussion-form-trigger');
  if (trigger) {
    discCta(trigger);
    side.appendChild(trigger);
  }
  const contact = node('div', 'bs-pd-disc__contact');
  // „Spěchá to? +420 …" jen s telefonem — bez něj by věta zůstala sirotkem
  if (phonesOn()) {
    const urgent = node('span', null, LABEL_DISC_URGENT + ' ');
    urgent.appendChild(link(telHref(CONTACT.phone), 'bs-pd-disc__phone', CONTACT.phone));
    contact.appendChild(urgent);
  }
  contact.appendChild(node('span', null, CONTACT.hours));
  side.appendChild(contact);

  const list = container.querySelector('#discussionsList');
  if (!list) return;
  list.classList.add('bs-pd-disc__list');
  const sync = function () {
    list.querySelectorAll('.vote-wrap').forEach(function (vote) {
      decoratePost(vote, list);
    });
  };
  sync();
  new MutationObserver(sync).observe(list, { childList: true, subtree: true });
}

// Karta značky (prototyp §15) z nativního popisu výrobce: první odstavec jako
// slib, `<ul>` jako dlaždice sortimentu, odkaz na značku jako CTA. Logo bere
// admin blok `#bs-brand-logos` (`data-bs-brand` = slug z URL značky); bez něj
// se slot nekreslí.
function brandTileIcon(title) {
  for (let i = 0; i < BRAND_TILE_ICONS.length; i += 1) {
    if (BRAND_TILE_ICONS[i][0].test(title)) return BRAND_TILE_ICONS[i][1];
  }
  return 'cog';
}

function buildBrand() {
  const pane = document.getElementById('manufacturerDescription');
  if (!pane) return;
  const brandLink = document.querySelector('.p-detail-inner-header .brand-wrapper a');
  const name = text(brandLink) || text(document.querySelector('.tab-manufacturer-name'));
  if (!name) return;

  const oldTitle = pane.querySelector(':scope > .bs-pd-sec__title');
  if (oldTitle) oldTitle.remove();
  const paragraphs = [...pane.querySelectorAll(':scope > p')];
  const list = pane.querySelector(':scope > ul');

  pane.classList.add('bs-pd-brand');
  const card = node('div', 'bs-pd-brand__card');
  const main = node('div', 'bs-pd-brand__main');

  const slug = brandLink ? (brandLink.getAttribute('href') || '').replace(/\/$/, '').split('/').pop() : '';
  const logoSrc = slug ? document.querySelector('#bs-brand-logos [data-bs-brand="' + slug + '"]') : null;
  if (logoSrc && logoSrc.getAttribute('data-bs-img')) {
    const logo = node('span', 'bs-pd-brand__logo');
    const img = node('img');
    img.src = logoSrc.getAttribute('data-bs-img');
    img.alt = name;
    img.loading = 'lazy';
    logo.appendChild(img);
    main.appendChild(logo);
  }
  const h2 = node('h2', 'bs-pd-brand__title', LABEL_BRAND_TITLE + ' ');
  h2.appendChild(node('em', null, name));
  main.appendChild(h2);
  if (paragraphs[0]) {
    paragraphs[0].classList.add('bs-pd-brand__lead');
    main.appendChild(paragraphs[0]);
  }
  if (brandLink) {
    const cta = link(brandLink.getAttribute('href'), 'bs-pd-brand__cta', LABEL_BRAND_ALL + ' ' + name);
    cta.appendChild(icon('arrow-right'));
    main.appendChild(cta);
  }
  card.appendChild(main);

  let tiles = 0;
  if (list) {
    const grid = node('div', 'bs-pd-brand__tiles');
    [...list.querySelectorAll(':scope > li')].slice(0, 4).forEach(function (li) {
      const strong = li.querySelector('strong');
      const title = text(strong);
      if (!title) return;
      const a = li.querySelector('a');
      const tile = a ? link(a.getAttribute('href'), 'bs-pd-brand__tile') : node('div', 'bs-pd-brand__tile');
      const ic = node('span', 'bs-pd-brand__tile-ic');
      ic.appendChild(icon(brandTileIcon(title)));
      tile.appendChild(ic);
      tile.appendChild(node('span', 'bs-pd-brand__tile-title', title));
      const desc = text(li).replace(title, '').replace(/^[\s:–—-]+/, '').trim();
      if (desc) tile.appendChild(node('span', 'bs-pd-brand__tile-desc', desc.charAt(0).toUpperCase() + desc.slice(1)));
      grid.appendChild(tile);
      tiles += 1;
    });
    if (tiles) card.appendChild(grid);
    list.remove();
  }
  if (!tiles) card.classList.add('bs-pd-brand__card--single');
  pane.insertAdjacentElement('afterbegin', card);

  // zbývající odstavce výrobce pod kartou (obsah se nezahazuje)
  const rest = paragraphs.slice(1).filter(function (p) {
    return text(p);
  });
  if (rest.length) {
    const more = node('div', 'bs-pd-brand__rest');
    rest.forEach(function (p) {
      more.appendChild(p);
    });
    pane.appendChild(more);
  }
  pane.querySelectorAll(':scope > p').forEach(function (p) {
    if (!text(p)) p.remove();
  });
}

// ---------------------------------------------------------------------------
// Kotvící navigace 7a (podtržené na střed) s počty + scroll-spy
// ---------------------------------------------------------------------------

// Mobilní lišta je sticky POD headerem (ten je sticky sám a při scrollu se
// kompaktuje) → jeho výšku držíme v CSS proměnné.
function trackHeaderHeight() {
  const header = document.getElementById('header');
  if (!header) return;
  const apply = function () {
    const h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--bs-pd-anchor-top', h + 'px');
  };
  // ⚠ Synchronní `apply()` četlo výšku headeru uprostřed bootu a hned zapisovalo
  // CSS proměnnou na `documentElement` — čtení/zápis v cyklu s RO dělalo 737 ms
  // self-time při CPU 4× (profil 2026-09-11, F1). ResizeObserver vydá první
  // observaci sám, po layoutu a ještě před paintem.
  if ('ResizeObserver' in window) new ResizeObserver(apply).observe(header);
  else {
    requestAnimationFrame(apply);
    window.addEventListener('resize', apply, { passive: true });
  }
}

function anchorCount(kind) {
  switch (kind) {
    case 'faq':
      return document.querySelectorAll('#poradna details, #poradna .bs-pc-faq__item').length || null;
    case 'rating':
      return ratingCount() || null; // „Neohodnoceno" nedostane chip 0
    case 'discussion': {
      const m = text(document.querySelector('.shp-tab-link[href="#productDiscussion"], .shp-accordion-link[href="#productDiscussion"]')).match(/\((\d+)\)/);
      if (m) return parseInt(m[1], 10);
      return document.querySelectorAll('#productDiscussion .discussion-post, #productDiscussion .vote-wrap').length;
    }
    case 'related':
      return document.querySelectorAll('.products-related .p[data-micro="product"]').length || null;
    case 'alternative':
      return document.querySelectorAll('.products-alternative .p[data-micro="product"]').length || null;
    default:
      return null;
  }
}

function buildAnchors(detail) {
  const bar = node('nav', 'bs-anchors');
  bar.setAttribute('aria-label', LABEL_NAV);
  const list = node('div', 'bs-anchors__list');
  const targets = [];

  const found = [];
  ANCHORS.forEach(function (item) {
    const target = document.getElementById(item.id);
    if (target && text(target)) found.push({ item: item, el: target });
  });
  // pilulky v pořadí sekcí na stránce (Shoptet řadí panely jinak než design,
  // aktivní položka by jinak při scrollu skákala mimo pořadí lišty)
  found.sort(function (a, b) {
    return a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });

  found.forEach(function (hit) {
    const pill = link('#' + hit.item.id, 'bs-anchors__item' + (hit.item.accent ? ' bs-anchors__item--accent' : ''));
    if (hit.item.icon) pill.appendChild(icon(hit.item.icon, 'bs-anchors__ic'));
    pill.appendChild(node('span', 'bs-anchors__label', hit.item.label));
    const count = hit.item.count ? anchorCount(hit.item.count) : null;
    if (count !== null && count !== undefined) pill.appendChild(node('span', 'bs-anchors__count', String(count)));
    list.appendChild(pill);
    hit.el.classList.add('bs-pd-anchor-target');
    targets.push({ el: hit.el, pill: pill });
  });

  if (!targets.length) return null;
  bar.appendChild(list);
  targets[0].pill.classList.add('is-active');

  const tabs = document.querySelector('.p-detail-tabs-wrapper');
  if (tabs) tabs.insertAdjacentElement('beforebegin', bar);
  else detail.insertAdjacentElement('afterend', bar);

  // aktivní položka podle toho, co je zrovna v horní části viewportu
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const hit = targets.filter(function (t) {
            return t.el === entry.target;
          })[0];
          if (!hit) return;
          if (entry.isIntersecting) {
            targets.forEach(function (t) {
              t.pill.classList.toggle('is-active', t === hit);
            });
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    targets.forEach(function (t) {
      io.observe(t.el);
    });
  }

  return bar;
}

// ---------------------------------------------------------------------------
// Související / podobné produkty — nativní drag slider ven, scroll-snap
// carousel karet výpisu dovnitř; hlavička s podtitulkem / „Zobrazit vše" a šipkami
// ---------------------------------------------------------------------------

function buildRelBar(holder, row, allHref, subtitle) {
  const title = holder.previousElementSibling;
  if (!title || !title.classList.contains('products-header')) return null;

  const bar = node('div', 'bs-prel__bar');
  title.insertAdjacentElement('beforebegin', bar);
  title.classList.add('bs-pd-sec__title');
  bar.appendChild(title);

  if (subtitle) bar.appendChild(node('span', 'bs-prel__sub', subtitle));
  if (allHref) bar.appendChild(link(allHref, 'bs-btn bs-btn--ghost bs-prel__all', LABEL_ROW_ALL));
  bar.appendChild(node('span', 'bs-prel__spacer'));

  row.nav = [
    { dir: -1, label: LABEL_ROW_PREV, glyph: 'arrow-left', cls: 'bs-prel__nav--prev' },
    { dir: 1, label: LABEL_ROW_NEXT, glyph: 'arrow-right', cls: 'bs-prel__nav--next' },
  ].map(function (spec) {
    const btn = node('button', 'bs-btn bs-btn--circle bs-prel__nav ' + spec.cls);
    btn.type = 'button';
    btn.setAttribute('aria-label', spec.label);
    btn.appendChild(icon(spec.glyph));
    btn.addEventListener('click', function () {
      const track = row.track;
      if (track) scrollTrack(track, track.scrollLeft + spec.dir * Math.max(240, track.clientWidth * 0.85));
    });
    bar.appendChild(btn);
    return btn;
  });

  return bar;
}

// Šipky odrážejí kraje pásu (a zhasnou úplně, když se všechno vejde).
function updateRelNav(row) {
  const track = row.track;
  if (!track || !row.nav) return;
  const max = track.scrollWidth - track.clientWidth;
  const set = function (btn, off) {
    btn.disabled = off;
    btn.classList.toggle('is-disabled', off);
  };
  set(row.nav[0], track.scrollLeft <= 4);
  set(row.nav[1], track.scrollLeft >= max - 4);
}

function enhanceRelated() {
  document.querySelectorAll('.products-related-wrapper, .products-alternative-wrapper').forEach(function (holder) {
    if (!holder.querySelector('.p[data-micro="product"]')) return;
    const isAlternative = holder.classList.contains('products-alternative-wrapper');
    holder.classList.add('bs-prel');
    holder.classList.remove('has-navigation');
    const slider = holder.querySelector('.products');
    if (slider) {
      slider.classList.add('bs-prel__viewport');
      slider.classList.remove('product-slider'); // hook nativního drag slideru
    }

    const row = { holder: holder, track: null, nav: null };

    function cleanup() {
      holder.removeAttribute('style'); // `overflow: hidden` + inline šířky z default JS
      if (slider) slider.removeAttribute('style');
      holder.querySelectorAll('.product-slider-pagination, .product-slider-navigation').forEach(function (n) {
        n.remove();
      });
      const track = holder.querySelector('.products-block') || slider;
      if (track) {
        if (row.track && row.track !== track) row.track.classList.remove('bs-prel__track');
        row.track = track;
        track.classList.add('bs-prel__track');
        track.removeAttribute('style');
        track.querySelectorAll(':scope > .product').forEach(function (p) {
          p.removeAttribute('style');
        });
      }
      // ⚠ `scrollWidth`/`clientWidth` hned po přestavbě carouselů vynucovalo layout
      // uprostřed bootu (896 ms self-time při CPU 4×, profil 2026-09-11). Šipky jen
      // přepínají `disabled`, takže rAF nic neposune — CLS beze změny.
      requestAnimationFrame(function () {
        updateRelNav(row);
      });
    }

    // „Zobrazit vše" má smysl u podobných produktů → nadřazená kategorie
    const parent = isAlternative ? parentCategoryLink() : null;
    const bar = buildRelBar(holder, row, parent ? parent.getAttribute('href') : null, isAlternative ? null : LABEL_ROW_SUB);

    cleanup();
    new MutationObserver(cleanup).observe(holder, { childList: true, subtree: true });
    // scroll nebublá → capture na holderu přežije výměnu tracku default JS
    holder.addEventListener(
      'scroll',
      function () {
        updateRelNav(row);
      },
      { capture: true, passive: true }
    );
    window.addEventListener(
      'resize',
      function () {
        updateRelNav(row);
      },
      { passive: true }
    );

    // kotva sedí na hlavičce, ať anchor lišta neodscrolluje pod ni
    const anchored = bar || holder;
    const id = isAlternative ? 'podobne' : 'souvisejici';
    if (!document.getElementById(id)) anchored.id = id;
  });
}

// ---------------------------------------------------------------------------
// Příchod odkazem s kotvou sekce (e-mail „Ohodnoťte koupené produkty" →
// `#ratingTab`, recenze U5). Se `scroll-behavior: smooth` prohlížeč kotvu při
// načtení dojíždí animací s cílem spočítaným na jejím startu a karta recenzí
// z `/specs`, vložená během animace nad sekce, sekci odsune (ostrá 7. 10. 2026:
// iPhone 13 o 414 px, nadpis hodnocení u spodní hrany, desktop o 80 px).
// Plynulý scroll (`html.bs-pd-smooth`, kliky v liště kotev) proto zapíná až
// vrácený `settle()` po vložení obsahu z `/specs`; ten kotvu dorovná skokem,
// dokud návštěvník sám nescrolloval. Bez kotvy v URL se zapíná hned.
// ---------------------------------------------------------------------------
function arrivalAnchor() {
  const root = document.documentElement;
  let target = null;
  try {
    const id = decodeURIComponent(window.location.hash.slice(1));
    target = id ? document.getElementById(id) : null;
  } catch (e) {
    target = null; // nevalidní %-sekvence v kotvě
  }
  if (!target) {
    root.classList.add('bs-pd-smooth');
    return function () {};
  }
  // klepnutí (souhlas s cookies) scroll není; kolečko, tah prstem a klávesy ano
  let moved = false;
  const stop = function () {
    moved = true;
  };
  ['wheel', 'touchmove', 'keydown'].forEach(function (type) {
    window.addEventListener(type, stop, { passive: true, once: true });
  });
  return function settle() {
    if (!moved && target.isConnected) target.scrollIntoView({ block: 'start' });
    root.classList.add('bs-pd-smooth');
  };
}

export function initProductDetail() {
  const detail = document.querySelector('.p-detail .p-detail-inner');
  if (!detail || detail.dataset.bsPd) return;
  detail.dataset.bsPd = '1';

  // recenze jedou v tomtéž `/specs` GETu jako Bestseller a konec akce — kód se
  // musí přihlásit dřív, než se na něj kdokoli zeptá (reviewHighlights.js)
  const reviewsOn = featureOn('reviewHighlights');
  const pdProduct = shoptetData('product');
  if (reviewsOn && pdProduct && pdProduct.code) wantReviews(pdProduct.code);
  // chat otevřený odtud předvyplní „Dotaz k produktu <název>: " (supportChat.js): agent
  // Lucy podle názvu najde produkt, o kterém zákazník píše. Název z dataLayeru = název
  // produktu v Shoptetu, jak ho nese feed pro Lucy (titulek stránky má „ - Jipos.cz").
  const pdName = pdProduct && typeof pdProduct.name === 'string' ? pdProduct.name.replace(/\s+/g, ' ').trim() : '';
  if (pdName) setChatTopic(LC.productTopic(pdName));

  const data = detail.querySelector('.p-data-wrapper');
  const gallery = detail.querySelector('.p-image-wrapper');
  const form = detail.querySelector('#product-detail-form');

  // karta „Recenze zákazníků" + citát u názvu; počet a průměr z nativního DOM,
  // ať sedí s panelem hodnocení (ten už stojí, openSections běží synchronně).
  // S přepínačem reviewHighlightsShop kartu doplní hodnocení obchodu z dat
  // patičky (`loadReviews`, žádný další dotaz): patro 3 a výplň druhého místa.
  // Čeká se na ně, jen když vlastní recenze a kupující řadu nezaplní; pak vrací
  // promise vložení karty (karta stojí nad sekcemi, kdo na ni čeká, ví kdy).
  function mountHighlights(reviews, code) {
    const votes = document.querySelector('#ratingTab .votes');
    const ctx = {
      gallery,
      data,
      head: data && data.querySelector('.bs-pd__head'),
      native: { count: ratingCount(), avg: ratingAverage(), share: votes ? ratingShare(votes) : null },
    };
    if (!featureOn('reviewHighlightsShop')) {
      if (reviews) mountReviewHighlights(reviews, ctx);
      return null;
    }
    if (!needsShopQuotes(reviews, ctx.native)) {
      ctx.fill = { shop: null, quotes: [] };
      mountReviewHighlights(reviews, ctx);
      return null;
    }
    return loadReviews().then(function (live) {
      ctx.fill = { shop: live ? live.shop : null, quotes: live ? shopQuotes(live.obecne, live.plan.quotes, code) : [] };
      mountReviewHighlights(reviews, ctx);
    });
  }

  detail.classList.add('bs-pd');
  if (data) data.classList.add('bs-pd__buy');
  if (gallery) gallery.classList.add('bs-pd__gallery');

  if (gallery) buildGallery(gallery, form);
  if (data) buildHeader(data, form);

  const image = gallery ? gallery.querySelector('.p-image') : null;
  if (detail.dataset.bsBestseller === '1') mountSticker(image);

  // obsah z `/specs` (karta recenzí nad sekcemi) → teprve po něm dorovnat kotvu
  let specsDone = null;
  if (form) {
    const card = buildBuyCard(form);
    const watch = createPriceWatch(form);
    enhanceCta(form, watch, card);
    mountWholesale(watch, card);
    // Omnibus řádek + graf vývoje ceny — subscribe AŽ PO enhanceCta,
    // viditelnost čte `card.discounted` z jeho renderu (priceHistory.js)
    mountPriceHistory(watch, card);
    // ikona dopravy zdarma (značka na fotce + řádek pod osou), přepínač freeShipMark
    mountFreeShipMark(card, image);
    // z indexu (`/specs`, jeden GET sdílený se sticky lištou): Bestseller kruh
    // v galerii + konec akční ceny; slot odkryje render CTA jen při reálné slevě.
    // Velkoobchodu jen Bestseller — akce v indexu je ceníku 1 (searchConfig.js)
    const product = shoptetData('product');
    if (product && product.code) {
      specsDone = specFor(product.code).then(function (item) {
        // karta recenzí i bez položky indexu (nový produkt): patro 3 obchodu.
        // Callback vrací promise karty (čeká-li na citace obchodu): řetěz `/specs`
        // tak skončí až s kartou nad sekcemi (dorovnání kotvy při příchodu, #514)
        const highlights = reviewsOn ? mountHighlights(item ? item.reviews : null, product.code) : null;
        if (!item) return highlights;
        if (item.bestseller === true) mountSticker(image);
        const toDate = getRetailConfig() && item.action && item.action.to;
        if (!toDate || !card || !card.saleSlot || card.saleSlot.firstChild) return highlights;
        const el = createSaleEnd(toDate, { long: true });
        if (!el) return highlights;
        card.saleSlot.appendChild(el);
        card.saleSlot.hidden = !card.discounted;
        card.saleSlot.addEventListener('bs:sale-end-expired', function () {
          card.saleSlot.hidden = true;
        });
        return highlights;
      });
    }
  }

  if (data) {
    const advisor = buildAdvisor(data);
    const meta = buildMeta(data);
    data.appendChild(advisor);
    data.appendChild(meta);
  }

  initShippingModal();
  initServiceModal();
  initUpsellQuickView();
  enhanceRelated();
  openSections();
  trackHeaderHeight();
  buildAnchors(detail);

  // Sekce i lišta stojí → teprve teď smí CSS skrýt nativní taby/akordeon.
  // Kdyby boot spadl dřív, zůstane uživateli funkční nativní přepínání.
  document.documentElement.classList.add('bs-pd-ready');

  // až teď existují i id doplněná JS (`souvisejici`, `podobne`)
  const settle = arrivalAnchor();
  if (specsDone) specsDone.then(settle, settle);
  else settle();
}
